import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Plus, Search, X, Truck, Package, CheckCircle2, FileText, ChevronDown, MoreHorizontal,
  Trash2, Minus, Link as LinkIcon, Loader2, MapPin, Phone, Mail, StickyNote, Camera, RefreshCw
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import {
  CustomerOrder,
  CustomerOrderStatus,
  fetchCustomerOrders,
  invoiceCustomerOrder,
  updateCustomerOrderStatus,
  updateOrderItemQuantity,
  removeOrderItem,
  addOrderItem,
  updateOrderInternalNotes,
  updateOrderPoNumber,
  updateOrderExpectedReturns,
  refreshOrderEstimate,
  orderStatusLabel,
  nextOrderStatus,
  orderPaymentState,
  getContainerBalances,
  updateOrderFulfillmentType,
  updateOrderDeliveryAddress,
  quoteCustomerOrderInvoice,
  OrderInvoiceQuote,
  OrderReturnInput,
  reopenOrderBilling,
  fetchOrderEvents,
  OrderEvent,
  sortOrderItems,
  CustomerOrderItem,
  DeliveryPhoto,
  uploadDeliveryPhoto,
  saveDeliveryPhotos,
  deleteDeliveryPhotoFile,
  setOrderNotificationsMuted,
  ExcessRefundMode,
} from '../../lib/customerOrders';
import RefundDestinationPicker from '../refunds/RefundDestinationPicker';
import RefundDestinationSelect from '../refunds/RefundDestinationSelect';
import { RefundDestination, describeRefundOutcome } from '../../lib/refunds';
import { fetchCustomerPricing, applyDeliveryOverrides, CustomerPricing, EMPTY_PRICING } from '../../lib/customerPricing';
import CameraCapture from '../CameraCapture';
import { blobUrlToFile } from '../../services/workflowPhotoService';
import { useImageViewer } from '../ImageViewerProvider';
import { Customer, getCustomer } from '../../lib/customers';
import { sendEmailWithFallback } from '../../lib/email';
import BookingCustomerInfo from '../booking/BookingCustomerInfo';
import CustomerDetailsModal from '../CustomerDetailsModal';
import BetaPaymentsModal from '../BetaPaymentsModal';
import { ModalStackProvider } from '../../contexts/ModalStackContext';
import { SidePanel, Row, fmtMoney } from './SidePanel';
import RecordPaymentModal from '../RecordPaymentModal';
import { NewOrderModal, ProductPickerOverlay, PickerStockItem } from './NewOrderModal';
import { OrderRow, PaymentBadge, STATUS_STYLES, orderStatusIcon } from './OrderRow';
import { orderAreaName, useServiceAreas } from '../../lib/orderAreas';
import { StoreShippingConfig, loadStoreShippingConfig } from './shippingConfig';
import { containerQtyForCheckout, depositLineName } from '../../lib/deposits';
import {
  DEFAULT_TERM_OPTIONS,
  TERM_LABELS,
  computeDueDate,
  termLabel,
  type InvoiceTermCode,
} from '../../lib/invoices/invoiceTerms';
import {
  ShippingMethod,
  StructuredAddress,
  resolveShippingBase,
  describeShippingPrice,
  computeShippingSurcharges,
} from '../../lib/storefront';
import CustomerAddressCards from '../CustomerAddressCards';

// Re-export so existing imports (OrdersDropdown) keep working.
export { NewOrderModal } from './NewOrderModal';
interface Props {
  businessId: string;
  /** Called after an order is invoiced so parents can refresh stock. */
  onInvoiced?: () => void;
  /** Increment to open the New Order modal from outside (quick-create menus). */
  newOrderSignal?: number;
}

type OrderFilter = CustomerOrderStatus | 'active' | 'unpaid';

const FILTERS: Array<{ id: OrderFilter; label: string }> = [
  { id: 'active', label: 'Active' },
  { id: 'new', label: 'New' },
  { id: 'confirmed', label: 'Confirmed' },
  // Covers both fulfillment types: delivery orders out on the truck AND
  // pickup orders staged ready — same underlying status.
  { id: 'out_for_delivery', label: 'Out / Ready' },
  { id: 'delivered', label: 'Delivered' },
  { id: 'unpaid', label: 'Unpaid' },
  { id: 'cancelled', label: 'Cancelled' },
];

export default function CustomerOrdersTab({ businessId, onInvoiced, newOrderSignal }: Props) {
  const [orders, setOrders] = useState<CustomerOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<OrderFilter>('active');
  const [search, setSearch] = useState('');
  const areas = useServiceAreas(businessId);
  const [selectedOrder, setSelectedOrder] = useState<CustomerOrder | null>(null);
  const [isNewOrderOpen, setIsNewOrderOpen] = useState(false);

  useEffect(() => {
    if (newOrderSignal) setIsNewOrderOpen(true);
  }, [newOrderSignal]);

  const loadOrders = useCallback(async () => {
    try {
      // "Unpaid" is a payment-dimension filter, so it needs every order.
      const fetchAs = filter === 'unpaid' ? 'all' : filter;
      const data = await fetchCustomerOrders(businessId, { status: fetchAs });
      setOrders(data);
    } catch (err) {
      console.error('Failed to load customer orders:', err);
    } finally {
      setLoading(false);
    }
  }, [businessId, filter]);

  useEffect(() => {
    setLoading(true);
    loadOrders();
  }, [loadOrders]);

  // Live pipeline: any change to the business's orders refreshes the list, so
  // new online orders are never missed while the page is open.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const channel = supabase
      .channel(`orders_page_${businessId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'customer_orders', filter: `business_id=eq.${businessId}` },
        () => {
          if (timer) clearTimeout(timer);
          timer = setTimeout(() => loadOrders(), 350);
        }
      )
      .subscribe();
    return () => {
      if (timer) clearTimeout(timer);
      void supabase.removeChannel(channel);
    };
  }, [businessId, loadOrders]);

  const filteredOrders = useMemo(() => {
    let list = orders;
    if (filter === 'active') {
      // Active = goods not yet delivered. Delivered-but-unpaid orders live
      // under the Unpaid filter instead.
      list = list.filter(o => o.status !== 'delivered' && o.status !== 'cancelled');
    } else if (filter === 'unpaid') {
      list = list.filter(o => {
        const pay = orderPaymentState(o);
        return o.status !== 'cancelled' && (pay === 'unpaid' || pay === 'invoice_due' || pay === 'partial');
      });
    }
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(o => {
      const customerName = o.customer ? `${o.customer.first_name ?? ''} ${o.customer.last_name ?? ''}` : '';
      const hay = [
        o.order_number,
        customerName,
        o.contact_name ?? '',
        o.contact_email ?? '',
        o.contact_phone ?? '',
        o.delivery_address ?? '',
        orderAreaName(o, areas) ?? '',
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [orders, search, filter, areas]);

  return (
    <div className="p-4 sm:p-6" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif' }}>
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Orders</h3>
        <button
          type="button"
          onClick={() => setIsNewOrderOpen(true)}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[10px] bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700 active:bg-blue-800 shadow-sm touch-manipulation"
          style={{ WebkitTapHighlightColor: 'transparent', minHeight: '36px' }}
        >
          <Plus className="h-4 w-4" /> New Order
        </button>
      </div>

      {/* Filters — search + status dropdown on one row */}
      <div className="py-3 flex items-center gap-2">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search order #, customer, address, area"
            className="w-full pl-9 pr-3 py-2.5 rounded-[10px] border border-gray-200 bg-white text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
          />
        </div>
        <div className="relative flex-none">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as OrderFilter)}
            className="appearance-none bg-white border border-gray-200 rounded-[10px] pl-3.5 pr-8 py-2.5 text-[13px] font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
          >
            {FILTERS.map(f => (
              <option key={f.id} value={f.id}>{f.label}</option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 text-gray-400 animate-spin" /></div>
      ) : filteredOrders.length === 0 ? (
        <div className="py-16 text-center">
          <Truck className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-600">No orders here yet</p>
          <p className="text-[13px] text-gray-400 mt-1">Orders placed online or by staff will appear in this pipeline.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredOrders.map(order => (
            <OrderRow key={order.id} order={order} onClick={() => setSelectedOrder(order)} />
          ))}
        </div>
      )}

      {selectedOrder && (
        <OrderDetailModal
          order={selectedOrder}
          businessId={businessId}
          onClose={() => setSelectedOrder(null)}
          onChanged={async () => {
            await loadOrders();
          }}
          onInvoiced={() => {
            loadOrders();
            onInvoiced?.();
          }}
        />
      )}

      {isNewOrderOpen && (
        <NewOrderModal
          businessId={businessId}
          onClose={() => setIsNewOrderOpen(false)}
          onCreated={() => {
            setIsNewOrderOpen(false);
            setFilter('active');
            loadOrders();
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Order detail
// ─────────────────────────────────────────────────────────────────────────────

export function OrderDetailModal({
  order: initialOrder,
  businessId,
  onClose,
  onChanged,
  onInvoiced,
  zBase = 100060,
}: {
  order: CustomerOrder;
  businessId: string;
  onClose: () => void;
  onChanged: () => Promise<void> | void;
  onInvoiced: () => void;
  /** Base z-index; raise when opened above another high-z modal (e.g. Customer Details). */
  zBase?: number;
}) {
  const [order, setOrder] = useState<CustomerOrder>(initialOrder);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState(initialOrder.internal_notes ?? '');
  const [poNumber, setPoNumber] = useState(initialOrder.po_number ?? '');
  // Deliver — one overlay; collect-now vs invoice-on-terms is chosen inside it.
  const [showDeliverPanel, setShowDeliverPanel] = useState(false);
  // 'deliver' = Deliver & Invoice (marks delivered); 'invoice_only' = bill now, deliver later.
  const [deliverMode, setDeliverMode] = useState<'deliver' | 'invoice_only'>('deliver');
  const [showAddItem, setShowAddItem] = useState(false);
  // Invoice viewer — same BetaPaymentsModal as bookings (standalone-invoice mode).
  const [showPayments, setShowPayments] = useState(false);
  // Take Payment — the standard payment modal (record / Stripe pay / tap to pay…).
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  // Full customer record so we can render the standard customer card.
  const [fullCustomer, setFullCustomer] = useState<Customer | null>(null);
  const [showCustomerDetails, setShowCustomerDetails] = useState(false);
  // Containers the customer currently holds — drivers need this at a glance.
  const [containersOnHand, setContainersOnHand] = useState<Array<{ stock_item_id: string; quantity: number; item_name: string }>>([]);
  // Planned container pickup (empties back / lost), editable while the order
  // is still uninvoiced. Persisted on the order (expected_returns) so it
  // survives closing/reopening and seeds Deliver & Invoice / Invoice now.
  const [pickup, setPickup] = useState<Record<string, { returned: string; lost: string }>>(() =>
    Object.fromEntries(
      (initialOrder.expected_returns || []).map(r => [r.stock_item_id, { returned: String(r.returned ?? 0), lost: String(r.lost ?? 0) }])
    )
  );
  const pickupSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [pickupQuote, setPickupQuote] = useState<OrderInvoiceQuote | null>(null);
  // Reopen billing: inline confirmation before the unwind runs. Collected
  // money goes back through the refund engine — wallet by default, or card /
  // cash / e-transfer — and the same box handles "void then cancel".
  const [showReopenConfirm, setShowReopenConfirm] = useState(false);
  const [reopenThenCancel, setReopenThenCancel] = useState(false);
  const [reopenRefund, setReopenRefund] = useState<RefundDestination>('wallet');
  const [reopenReference, setReopenReference] = useState('');
  // Header ⋯ menu — holds Cancel order / Reopen billing (off the sticky bottom).
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const headerMenuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!showHeaderMenu) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node;
      if (headerMenuRef.current && !headerMenuRef.current.contains(t)) setShowHeaderMenu(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [showHeaderMenu]);
  // Shopify-style timeline: full audit history of the order.
  const [events, setEvents] = useState<OrderEvent[]>([]);
  const [staffNames, setStaffNames] = useState<Record<string, string>>({});
  // Per-product refundable deposit (cents) — drives auto-synced deposit lines.
  const [depositCentsByItem, setDepositCentsByItem] = useState<Record<string, number>>({});
  const [depositLabelByItem, setDepositLabelByItem] = useState<Record<string, string | null>>({});
  // Item edits are optimistic; server writes run serialized in the background.
  const opQueue = useRef<Promise<void>>(Promise.resolve());
  const itemsSyncTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Change delivery method (uninvoiced orders): store shipping options + picker.
  const [showChangeFulfillment, setShowChangeFulfillment] = useState(false);
  const [shipConfig, setShipConfig] = useState<StoreShippingConfig | null>(null);
  const fulfillmentRef = useRef<HTMLDivElement | null>(null);

  const loadFullCustomer = useCallback(async () => {
    if (!initialOrder.customer_id) return;
    try {
      setFullCustomer(await getCustomer(initialOrder.customer_id));
    } catch {
      // Guest-style fallback below still shows the order's contact info.
    }
  }, [initialOrder.customer_id]);

  useEffect(() => { loadFullCustomer(); }, [loadFullCustomer]);

  // Customer custom/tier pricing (item prices, deposits, delivery charges) —
  // applied when adding items, syncing deposit lines, or changing the method.
  const [customerPricing, setCustomerPricing] = useState<CustomerPricing>(EMPTY_PRICING);
  useEffect(() => {
    if (!initialOrder.customer_id) { setCustomerPricing(EMPTY_PRICING); return; }
    fetchCustomerPricing(businessId, initialOrder.customer_id)
      .then(setCustomerPricing)
      .catch(() => setCustomerPricing(EMPTY_PRICING));
  }, [businessId, initialOrder.customer_id]);

  const loadContainers = useCallback(async () => {
    if (!initialOrder.customer_id) return;
    try {
      const { data: sites, error: siteError } = await supabase
        .from('customer_container_balances_by_address')
        .select('stock_item_id, customer_address_id, quantity')
        .eq('customer_id', initialOrder.customer_id);
      let qtyByItem: Record<string, number> = {};
      if (siteError || !sites) {
        const bals = (await getContainerBalances(initialOrder.customer_id)).filter(b => b.quantity > 0);
        qtyByItem = Object.fromEntries(bals.map(b => [b.stock_item_id, b.quantity]));
      } else {
        qtyByItem = containerQtyForCheckout(sites, {
          pickup: initialOrder.fulfillment_type === 'pickup',
          addressId: initialOrder.delivery_address_id || null,
        });
      }
      const heldIds = Object.entries(qtyByItem).filter(([, qty]) => qty > 0);
      if (heldIds.length === 0) { setContainersOnHand([]); return; }
      const { data: items } = await supabase
        .from('stock_items')
        .select('id, name')
        .in('id', heldIds.map(([id]) => id));
      const names = new Map((items || []).map(i => [i.id, i.name]));
      setContainersOnHand(heldIds.map(([id, quantity]) => ({
        stock_item_id: id,
        quantity,
        item_name: names.get(id) || 'Container',
      })));
    } catch {
      // Non-blocking — the panel simply doesn't render.
    }
  }, [initialOrder.customer_id]);

  useEffect(() => { loadContainers(); }, [loadContainers]);

  // Deposit rates for the products on the order (drives deposit-line auto-sync).
  useEffect(() => {
    const ids = Array.from(new Set(
      (order.items || [])
        .filter(i => ['product', 'rental'].includes(i.line_type ?? 'product') && i.stock_item_id)
        .map(i => i.stock_item_id as string)
    ));
    const missing = ids.filter(id => !(id in depositCentsByItem));
    if (missing.length === 0) return;
    supabase
      .from('stock_items')
      .select('id, deposit_cents, deposit_label')
      .in('id', missing)
      .then(({ data }) => {
        if (!data) return;
        setDepositCentsByItem(prev => ({
          ...prev,
          ...Object.fromEntries(data.map(r => [r.id, Number(r.deposit_cents) || 0])),
        }));
        setDepositLabelByItem(prev => ({
          ...prev,
          ...Object.fromEntries(data.map(r => [r.id, r.deposit_label || null])),
        }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.items]);

  const loadEvents = useCallback(async () => {
    try {
      const evs = await fetchOrderEvents(order.id);
      setEvents(evs);
      const actorIds = Array.from(new Set(evs.map(e => e.actor_id).filter(Boolean))) as string[];
      if (actorIds.length > 0) {
        const { data } = await supabase.rpc('get_staff_display_names', {
          p_business_id: businessId,
          p_user_ids: actorIds,
        });
        if (data) {
          setStaffNames(Object.fromEntries((data as Array<{ id: string; display_name: string }>).map(r => [r.id, r.display_name])));
        }
      }
    } catch {
      // Timeline is best-effort; the order itself still renders.
    }
  }, [order.id, businessId]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  // Once an invoice exists the money side is locked (items are billed); the
  // fulfillment side can still advance (e.g. a pay-at-checkout order that is
  // paid while still "new").
  const hasInvoice = Boolean(order.invoice_id);
  const invoiceVoided = order.invoice?.status === 'void';
  const editable = !hasInvoice && order.status !== 'cancelled';
  // Invoiced orders can cancel too — the cancel flow reopens billing first.
  const canCancel = order.status !== 'cancelled';

  const reload = useCallback(async () => {
    const { data } = await supabase
      .from('customer_orders')
      .select('*, items:customer_order_items(*), customer:customers(id, first_name, last_name, email, phone), invoice:invoices(id, invoice_number, status, subtotal_cents, discount_cents, tax_cents, total_cents, amount_paid_cents, amount_due_cents, tax_breakdown)')
      .eq('id', order.id)
      .single();
    if (data) setOrder(data as CustomerOrder);
    await onChanged();
    loadEvents();
  }, [order.id, onChanged, loadEvents]);

  const withBusy = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } catch (err: any) {
      alert(err?.message || 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  // ── Item edits: optimistic UI + serialized background sync ──────────────────
  // Steppers update the rows instantly; server writes queue up behind the
  // scenes, and one debounced pass then re-syncs deposit lines, re-quotes the
  // estimate, and reloads. Rows never jump because rendering is stably sorted.

  const enqueueItemOp = (fn: () => Promise<void>) => {
    opQueue.current = opQueue.current.then(fn).catch(async (err: any) => {
      alert(err?.message || 'Something went wrong');
      await reload(); // resync the optimistic state with the server truth
    });
    return opQueue.current;
  };

  /**
   * Deposit lines mirror the standard formula: new deposits = qty − swap.
   * The swap is the planned pickup staff entered on the order when there is
   * one, else the containers the customer already has on hand. Recomputed
   * after every product / pickup change; the invoice is the final word.
   */
  const syncDepositLines = async (
    items: CustomerOrderItem[],
    planned?: Record<string, { returned: string; lost: string }>
  ): Promise<boolean> => {
    let changed = false;
    // Rental lines carry container deposits too (dispenser deposits etc.).
    const products = items.filter(i => ['product', 'rental'].includes(i.line_type ?? 'product') && i.stock_item_id);
    const qtyByStock = new Map<string, number>();
    for (const p of products) {
      qtyByStock.set(p.stock_item_id as string, (qtyByStock.get(p.stock_item_id as string) || 0) + Number(p.quantity));
    }
    const onHand = new Map(containersOnHand.map(c => [c.stock_item_id, c.quantity]));
    const plan = planned ?? pickup;

    // Customer deposit override wins (0 = no deposit for this customer).
    const effectiveDeposit = (stockId: string) =>
      customerPricing.deposits[stockId] ?? (depositCentsByItem[stockId] || 0);

    const wanted = new Map<string, number>();
    for (const [stockId, qty] of qtyByStock) {
      const cents = effectiveDeposit(stockId);
      if (cents <= 0) continue;
      const plannedBack = plan[stockId] ? Math.max(0, parseInt(plan[stockId].returned) || 0) : null;
      const swapped = Math.min(plannedBack ?? Math.max(0, onHand.get(stockId) ?? 0), qty);
      const target = qty - swapped;
      if (target > 0) wanted.set(stockId, target);
    }

    const productNameFor = (stockId: string) =>
      products.find(p => p.stock_item_id === stockId)?.product_name ?? 'container';

    for (const line of items.filter(i => i.line_type === 'deposit')) {
      const key = line.deposit_stock_item_id || '';
      const target = wanted.get(key);
      if (!target) {
        await removeOrderItem(line.id);
        changed = true;
      } else {
        if (Number(line.quantity) !== target) {
          await updateOrderItemQuantity(line.id, target);
          changed = true;
        }
        const wantedName = depositLineName(depositLabelByItem[key], productNameFor(key));
        if (!order.invoice_id && line.product_name !== wantedName) {
          await supabase.from('customer_order_items').update({ product_name: wantedName }).eq('id', line.id);
          changed = true;
        }
        wanted.delete(key);
      }
    }

    for (const [stockId, target] of wanted) {
      await addOrderItem(order.id, businessId, {
        stock_item_id: null,
        product_name: depositLineName(depositLabelByItem[stockId], productNameFor(stockId)),
        quantity: target,
        unit_price: effectiveDeposit(stockId) / 100,
        line_type: 'deposit',
        deposit_stock_item_id: stockId,
      });
      changed = true;
    }
    return changed;
  };

  const afterItemsChanged = async () => {
    let { data } = await supabase
      .from('customer_orders')
      .select('*, items:customer_order_items(*)')
      .eq('id', order.id)
      .single();
    if (!data) return;
    if (await syncDepositLines((data.items || []) as CustomerOrderItem[])) {
      ({ data } = await supabase
        .from('customer_orders')
        .select('*, items:customer_order_items(*)')
        .eq('id', order.id)
        .single());
      if (!data) return;
    }
    await refreshOrderEstimate(data as CustomerOrder);
    await reload();
  };

  const scheduleItemsSync = () => {
    if (itemsSyncTimer.current) clearTimeout(itemsSyncTimer.current);
    itemsSyncTimer.current = setTimeout(() => enqueueItemOp(afterItemsChanged), 450);
  };

  // Planned pickup edits: optimistic, then persisted to the order and the
  // deposit preview lines re-synced (debounced so typing is smooth).
  const pickupPayload = (p: Record<string, { returned: string; lost: string }>): OrderReturnInput[] =>
    Object.entries(p).map(([stock_item_id, v]) => ({
      stock_item_id,
      returned: Math.max(0, parseInt(v.returned) || 0),
      lost: Math.max(0, parseInt(v.lost) || 0),
    }));

  const setPickupFor = (stockId: string, patch: Partial<{ returned: string; lost: string }>) => {
    setPickup(prev => {
      const next = { ...prev, [stockId]: { returned: prev[stockId]?.returned ?? '0', lost: prev[stockId]?.lost ?? '0', ...patch } };
      if (pickupSaveTimer.current) clearTimeout(pickupSaveTimer.current);
      pickupSaveTimer.current = setTimeout(() => {
        enqueueItemOp(async () => {
          await updateOrderExpectedReturns(order.id, pickupPayload(next));
          const { data } = await supabase
            .from('customer_orders')
            .select('*, items:customer_order_items(*)')
            .eq('id', order.id)
            .single();
          if (data) {
            await syncDepositLines((data.items || []) as CustomerOrderItem[], next);
            const { data: fresh } = await supabase
              .from('customer_orders')
              .select('*, items:customer_order_items(*)')
              .eq('id', order.id)
              .single();
            if (fresh) await refreshOrderEstimate(fresh as CustomerOrder);
          }
          await reload();
        });
      }, 500);
      return next;
    });
  };

  // Live deposit math for the planned pickup (server-quoted: swap, new
  // deposits, refund due) — only while the order is still uninvoiced.
  const pickupDepositItems = useMemo(
    () => (order.items || []).filter(
      i => ['product', 'rental'].includes(i.line_type ?? 'product') && i.stock_item_id
        && ((customerPricing.deposits[i.stock_item_id] ?? depositCentsByItem[i.stock_item_id] ?? 0) > 0)
    ),
    [order.items, customerPricing.deposits, depositCentsByItem]
  );
  useEffect(() => {
    if (hasInvoice || order.status === 'cancelled' || pickupDepositItems.length === 0) {
      setPickupQuote(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const q = await quoteCustomerOrderInvoice(order.id, pickupPayload(pickup));
        if (!cancelled) setPickupQuote(q);
      } catch { /* preview only */ }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.id, order.items, pickup, hasInvoice, order.status, pickupDepositItems.length]);

  const handleQty = (itemId: string, qty: number) => {
    const items = order.items || [];
    const target = items.find(i => i.id === itemId);
    if (!target) return;
    if (qty <= 0) {
      const productCount = items.filter(i => ['product', 'rental'].includes(i.line_type ?? 'product')).length;
      if (['product', 'rental'].includes(target.line_type ?? 'product') && productCount <= 1) {
        alert('An order needs at least one item. Cancel the order instead.');
        return;
      }
    }
    setOrder(o => ({
      ...o,
      items: (o.items || []).map(i => (i.id === itemId ? { ...i, quantity: qty } : i)).filter(i => Number(i.quantity) > 0),
    }));
    enqueueItemOp(async () => {
      if (qty <= 0) {
        await removeOrderItem(itemId);
        // Removing a rental line ends its pending agreement so nothing
        // activates or bills at delivery.
        if ((target.line_type ?? 'product') === 'rental' && target.stock_item_id) {
          const { error } = await supabase.rpc('cancel_order_rental', {
            p_order_id: order.id,
            p_stock_item_id: target.stock_item_id,
          });
          if (error) throw error;
        }
      } else {
        await updateOrderItemQuantity(itemId, qty);
      }
    });
    scheduleItemsSync();
  };

  const handleAddItem = (item: PickerStockItem) => {
    // Rental-only items (rentable, no sale price) go on as a rental line and
    // create the pending agreement now, exactly like the New Order modal.
    const asRental = item.rental_enabled === true && Number(item.rental_price_cents) > 0 && item.price == null;
    if (asRental) {
      if (!order.customer_id) {
        alert('Rentals need a customer on the order.');
        return;
      }
      if ((order.items || []).some(i => (i.line_type ?? 'product') === 'rental' && i.stock_item_id === item.id)) {
        alert(`${item.name} is already on this order as a rental. Remove that line first to change it.`);
        return;
      }
      const rate = Number(item.rental_price_cents) / 100;
      enqueueItemOp(async () => {
        await addOrderItem(order.id, businessId, {
          stock_item_id: item.id,
          product_name: `Rental — ${item.name} (first ${item.rental_interval === 'week' ? 'week' : 'month'})`,
          quantity: 1,
          unit_price: rate,
          line_type: 'rental',
        });
        const { error } = await supabase.rpc('create_rental_agreement', {
          p_business_id: businessId,
          p_customer_id: order.customer_id,
          p_stock_item_id: item.id,
          p_order_id: order.id,
          p_quantity: 1,
          p_rate_cents: Math.round(rate * 100),
        });
        if (error) throw error;
      });
      scheduleItemsSync();
      return;
    }
    // Picker stays open — the item flips to a highlighted stepper row so more
    // units (or other products) can be added in one pass.
    enqueueItemOp(() =>
      addOrderItem(order.id, businessId, {
        stock_item_id: item.id,
        product_name: item.name,
        quantity: 1,
        // Customer custom/tier price wins over catalog.
        unit_price: customerPricing.prices[item.id] ?? item.price ?? 0,
      })
    );
    scheduleItemsSync();
  };

  const handleStatus = (status: CustomerOrderStatus) =>
    withBusy(async () => {
      await updateCustomerOrderStatus(order.id, status);
      await reload();
    });

  // Unwind billing (void invoice, money back to the customer, restock,
  // unlink), then optionally cancel in the same motion.
  const handleReopenBilling = (opts?: { thenCancel?: boolean }) =>
    withBusy(async () => {
      const res = await reopenOrderBilling(order.id, {
        destination: reopenRefund,
        reference: reopenReference.trim() || null,
      });
      setShowReopenConfirm(false);
      setReopenThenCancel(false);
      if (opts?.thenCancel) await updateCustomerOrderStatus(order.id, 'cancelled');
      await reload();
      const summary = describeRefundOutcome({
        cardCents: res.card_refunded_cents ?? 0,
        walletCents: res.wallet_refunded_cents ?? 0,
        cashCents: res.cash_refunded_cents ?? 0,
        destination: reopenRefund,
      });
      if ((res.card_refunded_cents ?? 0) + (res.cash_refunded_cents ?? 0) + (res.wallet_refunded_cents ?? 0) > 0) {
        alert(`Invoice voided — ${summary}.`);
      }
    });

  const handleSavePo = async () => {
    const next = poNumber.trim();
    if (next === (order.po_number ?? '').trim()) return;
    try {
      await updateOrderPoNumber(order.id, next);
      setOrder(o => ({ ...o, po_number: next || null }));
    } catch (err: any) {
      alert(err?.message || 'Failed to save PO number');
      setPoNumber(order.po_number ?? '');
      throw err;
    }
  };

  const handleSaveNotes = () =>
    withBusy(async () => {
      await updateOrderInternalNotes(order.id, notes);
      await reload();
    });

  // ── Change delivery method (before invoicing) ──────────────────────────────
  // Postal code drives area pricing: prefer the structured checkout snapshot,
  // fall back to scraping a Canadian postal out of the plain address string.
  const POSTAL_RE = /[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d/;
  const orderPostal =
    order.shipping_address?.postal_code ||
    order.delivery_address?.match(POSTAL_RE)?.[0] ||
    '';

  // Pickup orders carry no address, so switching to delivery needs one. For
  // orders tied to a customer the address-book picker is used (its cards are
  // the source of truth and edits persist to the profile); guest orders fall
  // back to free-typed fields. The postal drives the live method pricing.
  const [manualAddress, setManualAddress] = useState('');
  const [manualPostal, setManualPostal] = useState('');
  const [pickedAddressId, setPickedAddressId] = useState<string | null>(null);
  const [pickedAddr, setPickedAddr] = useState<StructuredAddress | null>(null);
  const needsAddressInput = !order.delivery_address || !orderPostal;
  const hasCustomer = !!order.customer_id;
  const effPostal =
    orderPostal ||
    (hasCustomer ? (pickedAddr?.postal_code || '').trim() : manualPostal.trim());

  // Load shipping options as soon as the order is editable so the inline
  // "Change" dropdown on the delivery charge line has its options ready.
  useEffect(() => {
    if (!editable || shipConfig) return;
    loadStoreShippingConfig(businessId).then(setShipConfig).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable, businessId]);

  const openChangeFulfillment = async (opts?: { scroll?: boolean }) => {
    setShowChangeFulfillment(v => (opts?.scroll ? true : !v));
    if (opts?.scroll) {
      // Opened from the delivery charge line down in Items — bring the picker into view.
      setTimeout(() => fulfillmentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
    }
    if (!hasCustomer && needsAddressInput && !manualAddress && fullCustomer?.address) {
      setManualAddress(fullCustomer.address);
      setManualPostal(fullCustomer.address.match(POSTAL_RE)?.[0] || '');
    }
    if (!shipConfig) {
      try {
        setShipConfig(await loadStoreShippingConfig(businessId));
      } catch {
        setShipConfig(null);
      }
    }
  };

  const applyFulfillment = (choice: { kind: 'pickup' } | { kind: 'method'; method: ShippingMethod }) =>
    withBusy(async () => {
      // Old fee lines (base delivery + surcharges) are replaced wholesale.
      for (const it of (order.items || []).filter(i => (i.line_type ?? 'product') === 'fee')) {
        await removeOrderItem(it.id);
      }

      if (choice.kind === 'method') {
        // Switching to delivery needs somewhere to deliver to.
        const enteredAddr = hasCustomer
          ? (pickedAddr?.formatted || pickedAddr?.line1 || '')
          : manualAddress;
        const addr = (order.delivery_address || enteredAddr).trim();
        if (!addr) {
          throw new Error('Choose or add the delivery address first — this order was placed for pickup and has none on file.');
        }
        // Customer delivery override (by method name) replaces the base price.
        const effMethod = applyDeliveryOverrides([choice.method], customerPricing.delivery)[0];
        const base = resolveShippingBase(effMethod, { postal_code: effPostal }, shipConfig?.areas);
        if (base.status !== 'priced') {
          throw new Error('This method has area pricing and the order address is outside its rated areas.');
        }
        // Persist the chosen/entered address (and postal, for area pricing)
        // onto the order snapshot. Address-book cards were already saved to
        // the customer profile by the picker itself.
        if (!order.delivery_address || (!orderPostal && effPostal)) {
          await updateOrderDeliveryAddress(order.id, addr, effPostal || null);
        }
        if (base.price > 0) {
          await addOrderItem(order.id, businessId, {
            stock_item_id: null,
            product_name: `Delivery — ${choice.method.name}`,
            quantity: 1,
            unit_price: Math.round(base.price * 100) / 100,
            line_type: 'fee',
          });
        }
        const productLines = (order.items || []).filter(
          i => ['product', 'rental'].includes(i.line_type ?? 'product') && i.stock_item_id
        );
        const surcharges = computeShippingSurcharges(
          choice.method,
          productLines.map(i => ({
            product: { id: i.stock_item_id as string, name: i.product_name },
            quantity: Number(i.quantity),
          }))
        );
        for (const s of surcharges) {
          await addOrderItem(order.id, businessId, {
            stock_item_id: null,
            product_name: s.label,
            quantity: 1,
            unit_price: Math.round(s.amount * 100) / 100,
            line_type: 'fee',
          });
        }
        if (order.fulfillment_type !== 'delivery') await updateOrderFulfillmentType(order.id, 'delivery');
      } else {
        if (order.fulfillment_type !== 'pickup') await updateOrderFulfillmentType(order.id, 'pickup');
      }

      const { data } = await supabase
        .from('customer_orders')
        .select('*, items:customer_order_items(*)')
        .eq('id', order.id)
        .single();
      if (data) await refreshOrderEstimate(data as CustomerOrder);
      setShowChangeFulfillment(false);
      await reload();
    });

  const advance = nextOrderStatus(order.status);
  const contactName = order.customer
    ? `${order.customer.first_name ?? ''} ${order.customer.last_name ?? ''}`.trim() || order.contact_name
    : order.contact_name;

  const handleCancelOrder = () => {
    // Live invoice: cancelling unwinds billing first (invoice voided,
    // collected money → wallet credit, stock/containers restored).
    if (hasInvoice && !invoiceVoided) {
      const paidCents = Number(order.invoice?.amount_paid_cents || 0);
      if (paidCents > 0) {
        // Money was collected — staff choose where it goes back in the
        // reopen box (wallet / card / cash / e-transfer), then cancel.
        setReopenRefund(order.customer_id ? 'wallet' : 'card');
        setReopenThenCancel(true);
        setShowReopenConfirm(true);
        return;
      }
      if (confirm('Cancel this order? Its invoice will be voided and stock restored.')) {
        handleReopenBilling({ thenCancel: true });
      }
    } else if (confirm('Cancel this order?')) {
      handleStatus('cancelled');
    }
  };

  // Sticky-bottom payment summary: invoice numbers once billed, estimate before.
  const liveInvoice = order.invoice && order.invoice.status !== 'void' ? order.invoice : null;
  const summaryTotalCents = liveInvoice ? Number(liveInvoice.total_cents) : order.estimated_total_cents;
  const summaryPaidCents = liveInvoice ? Number(liveInvoice.amount_paid_cents) : 0;
  const summaryBalanceCents = liveInvoice ? Math.max(0, Number(liveInvoice.amount_due_cents)) : summaryTotalCents;

  return (
    <SidePanel onClose={onClose} width="md:w-[560px]" zIndex={zBase}>
        {/* Header — status only (payment status lives on the sticky bottom) */}
        <div className="bg-gray-50 border-b border-gray-200 px-5 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-[17px] font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>{order.order_number}</h3>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_STYLES[order.status]}`}>
                {orderStatusIcon(order.status, order.fulfillment_type)} {orderStatusLabel(order.status, order.fulfillment_type)}
              </span>
            </div>
            <p className="text-[12px] text-gray-400 mt-0.5">
              Placed {new Date(order.placed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · via {order.source}
            </p>
          </div>
          <div className="flex items-center gap-1">
            {/* ⋯ menu — Cancel order / Reopen billing live here, not the footer */}
            {(canCancel || (hasInvoice && !invoiceVoided && order.status !== 'cancelled')) && (
              <div className="relative" ref={headerMenuRef}>
                <button
                  type="button"
                  onClick={() => setShowHeaderMenu(v => !v)}
                  aria-label="Order menu"
                  className="p-2 rounded-full hover:bg-gray-100"
                >
                  <MoreHorizontal className="w-5 h-5 text-gray-500" />
                </button>
                {showHeaderMenu && (
                  <div className="absolute right-0 top-full mt-1 w-56 rounded-2xl border border-gray-200 bg-white shadow-xl overflow-hidden py-1 z-30">
                    {hasInvoice && !invoiceVoided && order.status !== 'cancelled' && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => {
                          setShowHeaderMenu(false);
                          setReopenThenCancel(false);
                          setReopenRefund(order.customer_id ? 'wallet' : 'card');
                          setShowReopenConfirm(true);
                        }}
                        className="flex w-full items-center px-4 py-2.5 text-left text-[13px] font-medium text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                      >
                        Reopen billing to edit order
                      </button>
                    )}
                    {canCancel && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => {
                          setShowHeaderMenu(false);
                          handleCancelOrder();
                        }}
                        className="flex w-full items-center px-4 py-2.5 text-left text-[13px] font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                      >
                        Cancel order
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
            <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100"><X className="w-5 h-5 text-gray-500" /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 md:px-5 py-4 space-y-4 bg-gray-50/50">
          {(editable || !!order.po_number) && (
            <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] p-3.5">
              <label htmlFor="order-po-number" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                PO number
              </label>
              {editable ? (
                <input
                  id="order-po-number"
                  value={poNumber}
                  onChange={(e) => setPoNumber(e.target.value)}
                  onBlur={handleSavePo}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
                  }}
                  placeholder="Optional"
                  autoComplete="off"
                  className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                />
              ) : (
                <p className="text-[14px] font-medium text-gray-900">{order.po_number}</p>
              )}
            </div>
          )}
          {/* Order details: prominent fulfillment type + contact + address */}
          {(() => {
            const isDelivery = order.fulfillment_type === 'delivery';
            // Delivery method name comes from the order's base delivery fee line.
            const methodLine = isDelivery
              ? (order.items || []).find(i => (i.line_type ?? 'product') === 'fee' && !/surcharge/i.test(i.product_name))
              : null;
            const methodName = methodLine ? methodLine.product_name.replace(/^Delivery — /, '') : null;
            return (
              <div ref={fulfillmentRef} className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] p-3.5 space-y-1.5">
                <div className="flex items-center gap-2 pb-2 mb-1 border-b border-gray-100">
                  <span className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                    {isDelivery ? <Truck className="w-4 h-4 text-gray-600" /> : <Package className="w-4 h-4 text-gray-600" />}
                  </span>
                  <span className="min-w-0 leading-tight">
                    <span className="block text-[15px] font-bold text-gray-900">
                      {isDelivery ? 'Delivery' : 'Pickup'}
                    </span>
                    {methodName && (
                      <span className="block text-[12px] text-gray-500 truncate">{methodName}</span>
                    )}
                  </span>
                  <span className="ml-auto flex items-center gap-2">
                    {(order.requested_date || order.requested_window) && (
                      <span className="text-[12px] text-gray-500">
                        {[order.requested_date, order.requested_window].filter(Boolean).join(' · ')}
                      </span>
                    )}
                    {editable && (
                      <button
                        onClick={() => openChangeFulfillment()}
                        disabled={busy}
                        className="text-[12px] font-semibold text-blue-600 hover:text-blue-700 disabled:opacity-50"
                      >
                        {showChangeFulfillment ? 'Close' : 'Change'}
                      </button>
                    )}
                  </span>
                </div>

                {/* Delivery method picker — replaces the order's fee lines */}
                {showChangeFulfillment && editable && (
                  <div className="rounded-lg bg-gray-50 border border-gray-100 p-2.5 space-y-1.5 mb-1">
                    <p className="text-[11px] font-bold text-gray-700 uppercase tracking-wide">Change delivery method</p>
                    {!shipConfig ? (
                      <div className="flex items-center gap-2 text-[12px] text-gray-500 py-1">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading options…
                      </div>
                    ) : (
                      <>
                        {/* Pickup orders have no address on file — choose one from the
                            customer's address book (edits persist to their profile) so
                            delivery methods can quote and the driver has a stop. */}
                        {needsAddressInput && shipConfig.shippingMethods.length > 0 && (
                          <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-2 space-y-1.5">
                            <p className="text-[11px] font-semibold text-blue-800">Deliver to</p>
                            {hasCustomer ? (
                              <CustomerAddressCards
                                businessId={businessId}
                                customerId={order.customer_id as string}
                                mode="pick"
                                selectedId={pickedAddressId}
                                onSelect={(card, structured) => {
                                  setPickedAddressId(card?.id ?? null);
                                  setPickedAddr(structured);
                                }}
                                areaPrefixes={shipConfig.enforced && shipConfig.enforcedPrefixes.length > 0 ? shipConfig.enforcedPrefixes : null}
                                prefillLine1={fullCustomer?.address || null}
                              />
                            ) : (
                              <>
                                <input
                                  type="text"
                                  value={manualAddress}
                                  onChange={(e) => setManualAddress(e.target.value)}
                                  placeholder="Street address, city"
                                  className="w-full px-2.5 py-1.5 text-[13px] border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                                />
                                <input
                                  type="text"
                                  value={manualPostal}
                                  onChange={(e) => setManualPostal(e.target.value.toUpperCase())}
                                  placeholder="Postal code (prices the delivery area)"
                                  className="w-40 px-2.5 py-1.5 text-[13px] border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                                />
                              </>
                            )}
                          </div>
                        )}
                        {applyDeliveryOverrides(shipConfig.shippingMethods, customerPricing.delivery).map((m, idx) => {
                          const desc = describeShippingPrice(m, { postal_code: effPostal }, shipConfig.areas);
                          const disabled = desc.state === 'unavailable' || desc.state === 'needs_address' || busy;
                          return (
                            <button
                              key={idx}
                              disabled={disabled}
                              onClick={() => applyFulfillment({ kind: 'method', method: m })}
                              className="w-full flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2 text-left hover:border-blue-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <span className="text-[13px] font-medium text-gray-900">{m.name}</span>
                              <span className={`text-[12px] font-semibold ${desc.state === 'unavailable' ? 'text-red-500' : 'text-gray-700'}`}>
                                {desc.state === 'needs_address' ? 'Enter postal code above' : desc.label}
                              </span>
                            </button>
                          );
                        })}
                        {shipConfig.enableInStorePickup && (
                          <button
                            disabled={busy}
                            onClick={() => applyFulfillment({ kind: 'pickup' })}
                            className="w-full flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2 text-left hover:border-emerald-300 disabled:opacity-50"
                          >
                            <span className="text-[13px] font-medium text-gray-900">In-store pickup</span>
                            <span className="text-[12px] font-semibold text-emerald-600">FREE</span>
                          </button>
                        )}
                        {shipConfig.shippingMethods.length === 0 && !shipConfig.enableInStorePickup && (
                          <p className="text-[12px] text-gray-500">No delivery methods configured in Online Store settings.</p>
                        )}
                        <p className="text-[11px] text-gray-400">
                          Switching replaces the order's delivery fee (and any surcharges) and re-quotes the totals.
                        </p>
                      </>
                    )}
                  </div>
                )}
                {/* Customer block — the standard customer card (call / chat / email)
                    lives INSIDE the delivery container so contact info isn't
                    duplicated in a second card below. Guests keep text lines. */}
                {fullCustomer ? (
                  <div className="pt-0.5 pb-1">
                    <BookingCustomerInfo
                      customer={fullCustomer}
                      businessId={businessId}
                      onOpenCustomerDetails={() => setShowCustomerDetails(true)}
                      canEdit={false}
                    />
                  </div>
                ) : (
                  <>
                    {contactName && <p className="text-[13px] font-semibold text-gray-900">{contactName}</p>}
                    {(order.contact_phone || order.customer?.phone) && (
                      <p className="text-[12px] text-gray-600 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-gray-400" />{order.contact_phone || order.customer?.phone}</p>
                    )}
                    {(order.contact_email || order.customer?.email) && (
                      <p className="text-[12px] text-gray-600 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-gray-400" />{order.contact_email || order.customer?.email}</p>
                    )}
                  </>
                )}
                {isDelivery ? (
                  order.delivery_address && (
                    <p className="text-[12px] text-gray-700 flex items-start gap-1.5">
                      <MapPin className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <span><span className="font-medium">Deliver to:</span> {order.delivery_address}</span>
                    </p>
                  )
                ) : (
                  <p className="text-[12px] text-gray-700 flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    Customer picks up in store — no delivery.
                  </p>
                )}
                {order.customer_note && (
                  <p className="text-[12px] text-amber-700 bg-amber-50 rounded-lg px-2.5 py-1.5 flex items-start gap-1.5 mt-1">
                    <StickyNote className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />{order.customer_note}
                  </p>
                )}

                {/* Containers the customer holds — what the driver should expect back */}
                {containersOnHand.length > 0 && (
                  <div className="mt-2 rounded-lg bg-gray-50 border border-gray-100 px-3 py-2">
                    <p className="text-[11px] font-bold text-gray-700 uppercase tracking-wide flex items-center gap-1.5 mb-1">
                      <Package className="w-3.5 h-3.5" /> Containers on hand
                    </p>
                    {containersOnHand.map(c => (
                      <p key={c.stock_item_id} className="text-[13px] font-semibold text-gray-900">
                        {c.quantity} × {c.item_name}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Items */}
          <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] p-4">
            <div className="flex items-center justify-between mb-2.5">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Items</h4>
              {editable && (
                <button onClick={() => setShowAddItem(true)} className="text-[12px] font-semibold text-blue-600 hover:text-blue-700 inline-flex items-center gap-1">
                  <Plus className="w-3.5 h-3.5" /> Add product
                </button>
              )}
            </div>
            {(() => {
              // One consistent row style for everything; the groups (Items /
              // Deposits / Delivery) do the separating instead of colors.
              const all = sortOrderItems(order.items || []);
              const lineOf = (i: CustomerOrderItem) => i.line_type ?? 'product';
              const productLines = all.filter(i => ['product', 'rental'].includes(lineOf(i)));
              const depositLines = all.filter(i => lineOf(i) === 'deposit');
              const feeLines = all.filter(i => lineOf(i) === 'fee');
              const rowClass = 'flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50/50 px-3 py-2.5';
              const settleAt = order.fulfillment_type === 'pickup' ? 'pickup' : 'delivery';
              return (
                <>
                  <div className="space-y-2">
                    {productLines.map(item => lineOf(item) === 'rental' ? (
                      // Rental: qty locked to the agreement — remove and re-add to change.
                      <div key={item.id} className={rowClass}>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-medium text-gray-900 truncate">
                            {item.product_name.replace(/^Rental — /, '')}
                          </p>
                          <p className="text-[12px] text-gray-500">Rental · recurring billing starts at {settleAt}</p>
                        </div>
                        <span className="text-[13px] font-semibold text-gray-900 flex-shrink-0">
                          {Number(item.quantity)} × ${Number(item.unit_price).toFixed(2)}
                        </span>
                        {editable && (
                          <button onClick={() => handleQty(item.id, 0)} className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-red-50 active:scale-95 transition flex-shrink-0">
                            <Trash2 className="w-3.5 h-3.5 text-red-500" />
                          </button>
                        )}
                      </div>
                    ) : (
                      <div key={item.id} className={rowClass}>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-medium text-gray-900 truncate">{item.product_name}</p>
                          <p className="text-[12px] text-gray-500">${Number(item.unit_price).toFixed(2)} each</p>
                        </div>
                        {editable ? (
                          <div className="flex items-center gap-1.5">
                            <button onClick={() => handleQty(item.id, Number(item.quantity) - 1)} className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 active:scale-95 transition">
                              <Minus className="w-3.5 h-3.5 text-gray-600" />
                            </button>
                            <span className="w-8 text-center text-[13px] font-semibold text-gray-900">{Number(item.quantity)}</span>
                            <button onClick={() => handleQty(item.id, Number(item.quantity) + 1)} className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 active:scale-95 transition">
                              <Plus className="w-3.5 h-3.5 text-gray-600" />
                            </button>
                            <button onClick={() => handleQty(item.id, 0)} className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-red-50 ml-1 active:scale-95 transition">
                              <Trash2 className="w-3.5 h-3.5 text-red-500" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-[13px] font-semibold text-gray-900">× {Number(item.quantity)}</span>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Container pickup — planned empties back, editable until invoiced.
                      Drives the deposit preview lines below and seeds invoicing. */}
                  {editable && pickupDepositItems.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 flex items-center gap-1.5">
                        <RefreshCw className="w-3.5 h-3.5" /> Container pickup
                      </h4>
                      <p className="text-[11px] text-gray-500 mb-2">
                        Empties the customer is handing back at {settleAt}. Deposit is only charged on containers they keep; extra empties are refunded.
                      </p>
                      <div className="space-y-2">
                        {Array.from(
                          pickupDepositItems.reduce((m, i) => {
                            const id = i.stock_item_id as string;
                            const cur = m.get(id) ?? { name: i.product_name.replace(/^Rental — /, '').replace(/ \(first (week|month)\)$/, ''), qty: 0 };
                            cur.qty += Number(i.quantity);
                            m.set(id, cur);
                            return m;
                          }, new Map<string, { name: string; qty: number }>()).entries()
                        ).map(([stockId, info]) => {
                          const onHand = containersOnHand.find(c => c.stock_item_id === stockId)?.quantity ?? 0;
                          const cur = pickup[stockId] ?? { returned: '0', lost: '0' };
                          const r = Math.max(0, parseInt(cur.returned) || 0);
                          const q = pickupQuote?.deposits.find(d => d.stock_item_id === stockId);
                          const rate = customerPricing.deposits[stockId] ?? depositCentsByItem[stockId] ?? 0;
                          return (
                            <div key={stockId} className="rounded-xl border border-gray-100 bg-gray-50/50 px-3 py-2.5 space-y-2">
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-[13px] font-medium text-gray-900 truncate">{info.name}</span>
                                <span className="text-[11px] text-gray-500 whitespace-nowrap">{info.qty} going out · {fmtMoney(rate)}/deposit</span>
                              </div>
                              <p className={`text-[12px] font-semibold ${onHand > 0 ? 'text-blue-700' : 'text-gray-500'}`}>
                                {order.customer_id
                                  ? `Customer has ${onHand} on record${onHand > 0 ? '' : ' — every empty picked up still refunds'}`
                                  : 'Guest — every empty handed back refunds at today\u2019s rate'}
                              </p>
                              <div className="flex flex-wrap gap-1.5">
                                {onHand > 0 && (
                                  <button type="button" onClick={() => setPickupFor(stockId, { returned: String(Math.min(info.qty, onHand)), lost: '0' })}
                                    className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100">
                                    Full swap ({Math.min(info.qty, onHand)} back)
                                  </button>
                                )}
                                {onHand > 0 && onHand !== Math.min(info.qty, onHand) && (
                                  <button type="button" onClick={() => setPickupFor(stockId, { returned: String(onHand), lost: '0' })}
                                    className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white text-gray-600 border border-gray-200 hover:bg-gray-50">
                                    All {onHand} back
                                  </button>
                                )}
                                <button type="button" onClick={() => setPickupFor(stockId, { returned: String(info.qty), lost: '0' })}
                                  className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white text-gray-600 border border-gray-200 hover:bg-gray-50">
                                  No deposit ({info.qty} back)
                                </button>
                                <button type="button" onClick={() => setPickupFor(stockId, { returned: '0', lost: '0' })}
                                  className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white text-gray-600 border border-gray-200 hover:bg-gray-50">
                                  None
                                </button>
                              </div>
                              <div className="flex items-center gap-3">
                                <label className="flex-1">
                                  <span className="block text-[11px] text-gray-500 mb-1">Empties returned</span>
                                  <input
                                    type="number"
                                    min="0"
                                    value={cur.returned}
                                    onChange={e => setPickupFor(stockId, { returned: e.target.value })}
                                    className="w-full px-3 py-2 text-[13px] border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                                  />
                                </label>
                                <label className="flex-1">
                                  <span className="block text-[11px] text-gray-500 mb-1">Lost (deposit kept)</span>
                                  <input
                                    type="number"
                                    min="0"
                                    value={cur.lost}
                                    onChange={e => setPickupFor(stockId, { lost: e.target.value })}
                                    className="w-full px-3 py-2 text-[13px] border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                                  />
                                </label>
                              </div>
                              {q && (
                                <p className="text-[11px] text-gray-500">
                                  {q.net_new > 0
                                    ? `${q.net_new} new deposit${q.net_new === 1 ? '' : 's'} charged (${fmtMoney(q.net_new * q.deposit_cents)})`
                                    : q.excess_refund_cents > 0
                                    ? `${fmtMoney(q.excess_refund_cents)} deposit refund due`
                                    : 'Even swap — no new deposit'}
                                  {(q.unmatched ?? 0) > 0 && ` · ${q.unmatched} of the ${r} aren\u2019t on record — refunded at today\u2019s ${fmtMoney(q.unmatched_rate_cents ?? q.deposit_cents)} rate`}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Deposits — computed lines that track product quantities */}
                  {depositLines.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Deposits</h4>
                      <div className="space-y-2">
                        {depositLines.map(item => (
                          <div key={item.id} className={rowClass}>
                            <div className="flex-1 min-w-0">
                              <p className="text-[13px] font-medium text-gray-900 truncate">
                                {item.product_name}
                              </p>
                              <p className="text-[12px] text-gray-500">Follows quantity and pickup · finalized when invoiced</p>
                            </div>
                            <span className="text-[13px] font-semibold text-gray-900 flex-shrink-0">
                              {Number(item.quantity)} × ${Number(item.unit_price).toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Delivery — fee lines; tapping opens the method picker */}
                  {feeLines.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Delivery</h4>
                      <div className="space-y-2">
                        {feeLines.map(item => {
                          const lineTotal = Number(item.unit_price) * Number(item.quantity);
                          const isSurcharge = /surcharge/i.test(item.product_name);
                          const canSwitch = editable && !isSurcharge && !!shipConfig;
                          const inner = (
                            <>
                              <div className="flex-1 min-w-0">
                                <p className="text-[13px] font-medium text-gray-900 truncate">
                                  {item.product_name.replace(/^Delivery — /, '')}
                                </p>
                                <p className="text-[12px] text-gray-500">{isSurcharge ? 'Surcharge' : 'Delivery charge'}</p>
                              </div>
                              <span className="text-[13px] font-semibold text-gray-900 flex-shrink-0">
                                {fmtMoney(Math.round(lineTotal * 100))}
                              </span>
                              {canSwitch && (
                                <span className="text-[12px] font-semibold text-blue-600 flex-shrink-0">Change</span>
                              )}
                            </>
                          );
                          if (!canSwitch) {
                            return (
                              <div key={item.id} className={rowClass}>
                                {inner}
                              </div>
                            );
                          }
                          // Invisible native <select> over the row — tapping it opens
                          // the OS picker right here instead of scrolling to the top.
                          const methods = applyDeliveryOverrides(shipConfig.shippingMethods, customerPricing.delivery);
                          return (
                            <div key={item.id} className={`relative ${rowClass} ${busy ? 'opacity-50' : ''}`}>
                              {inner}
                              <select
                                value=""
                                disabled={busy}
                                aria-label="Change delivery method"
                                onChange={(e) => {
                                  const v = e.target.value;
                                  if (v === 'pickup') applyFulfillment({ kind: 'pickup' });
                                  else if (v.startsWith('m:')) applyFulfillment({ kind: 'method', method: methods[Number(v.slice(2))] });
                                }}
                                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                              >
                                <option value="" disabled hidden>Change delivery method</option>
                                {methods.map((m, idx) => {
                                  const desc = describeShippingPrice(m, { postal_code: effPostal }, shipConfig.areas);
                                  const unavailable = desc.state === 'unavailable' || desc.state === 'needs_address';
                                  return (
                                    <option key={idx} value={`m:${idx}`} disabled={unavailable}>
                                      {m.name} — {desc.state === 'needs_address' ? 'needs address' : desc.label}
                                    </option>
                                  );
                                })}
                                {shipConfig.enableInStorePickup && (
                                  <option value="pickup">In-store pickup — FREE</option>
                                )}
                              </select>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          {/* Proof-of-delivery photos — captured in the Deliver step; shown here
              once the order is delivered (or add them for invoiced orders that
              were marked delivered directly, skipping the Deliver panel). */}
          {(order.status === 'delivered' || (order.delivery_photos?.length ?? 0) > 0) && (
            <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] p-4">
              <DeliveryPhotosSection order={order} compact />
            </div>
          )}

          {/* Internal notes */}
          <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] p-4">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Internal notes</h4>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onBlur={() => { if (notes !== (order.internal_notes ?? '')) handleSaveNotes(); }}
              rows={2}
              placeholder="Only visible to your team"
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 resize-none"
            />
          </div>

          {/* Reopen billing — confirmation box, opened from the header ⋯ menu */}
          {showReopenConfirm && order.invoice && (() => {
            const paidCents = Number(order.invoice.amount_paid_cents || 0);
            return (
              <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-3 space-y-2">
                <p className="text-[13px] font-semibold text-amber-900">
                  {reopenThenCancel ? 'Cancel this order and void its invoice?' : 'Reopen billing on this order?'}
                </p>
                <ul className="text-[12px] text-amber-800 space-y-1 list-disc pl-4">
                  <li>Invoice {order.invoice.invoice_number || ''} is voided (kept on record).</li>
                  <li>Stock and container movements from this billing are restored.</li>
                  {reopenThenCancel ? (
                    <li>The order is cancelled.</li>
                  ) : (
                    <li>The order unlocks — edit items, then Deliver &amp; Invoice again. Wallet credit re-applies automatically.</li>
                  )}
                </ul>
                {paidCents > 0 && (
                  <div className="pt-1 space-y-1.5">
                    <p className="text-[12px] font-semibold text-amber-900">
                      {fmtMoney(paidCents)} already collected — where does it go back?
                    </p>
                    <RefundDestinationPicker
                      businessId={order.business_id}
                      customerId={order.customer_id}
                      invoiceId={order.invoice.id}
                      amountCents={paidCents}
                      value={reopenRefund}
                      onChange={next => setReopenRefund(next as RefundDestination)}
                      destinations={order.customer_id ? ['wallet', 'card', 'cash', 'e_transfer'] : ['card', 'cash', 'e_transfer']}
                      hints={{ wallet: 'store credit — re-applies when you invoice again' }}
                      reference={reopenReference}
                      onReferenceChange={setReopenReference}
                      compact
                    />
                    {!order.customer_id && (
                      <p className="text-[11px] text-amber-800/80">
                        Guest order: no wallet to credit, so the money must go back to card or be handed over.
                      </p>
                    )}
                  </div>
                )}
                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => handleReopenBilling({ thenCancel: reopenThenCancel })}
                    className="flex-1 py-2 rounded-lg bg-amber-600 text-white text-[12px] font-semibold hover:bg-amber-700 disabled:opacity-50"
                  >
                    {reopenThenCancel ? 'Void & cancel order' : 'Reopen billing'}
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => { setShowReopenConfirm(false); setReopenThenCancel(false); }}
                    className="flex-1 py-2 rounded-lg bg-white border border-amber-200 text-amber-800 text-[12px] font-semibold hover:bg-amber-100/50 disabled:opacity-50"
                  >
                    Keep invoice
                  </button>
                </div>
              </div>
            );
          })()}

          {/* Timeline — Shopify-style audit history of everything on the order */}
          <OrderTimeline events={events} order={order} staffNames={staffNames} />
        </div>

        {/* Sticky bottom — payment summary (Total / Paid / Balance + invoice
            status and viewer) with the primary action underneath. */}
        <div className="px-5 py-3 border-t border-gray-100 flex-shrink-0 space-y-2.5 bg-white">
          <div className="flex items-center gap-4">
            <div>
              <p className="text-[11px] font-medium text-gray-400">Total{!liveInvoice ? ' (est.)' : ''}</p>
              <p className="text-[15px] font-bold text-gray-900 leading-tight">{fmtMoney(summaryTotalCents)}</p>
            </div>
            <div>
              <p className="text-[11px] font-medium text-gray-400">Paid</p>
              <p className={`text-[15px] font-bold leading-tight ${summaryPaidCents > 0 ? 'text-green-600' : 'text-gray-900'}`}>{fmtMoney(summaryPaidCents)}</p>
            </div>
            <div>
              <p className="text-[11px] font-medium text-gray-400">Balance</p>
              <p className={`text-[15px] font-bold leading-tight ${summaryBalanceCents > 0 ? 'text-red-600' : 'text-green-600'}`}>{fmtMoney(summaryBalanceCents)}</p>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <PaymentBadge order={order} />
              {order.invoice_id && (
                <button
                  type="button"
                  onClick={() => setShowPayments(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 active:scale-[0.98] transition touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <FileText className="h-3.5 w-3.5" />
                  Invoice
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            {/* Notifications mute — compact, always visible for the order cycle */}
            <label className="flex flex-shrink-0 cursor-pointer select-none items-center gap-2 pl-0.5">
              <input
                type="checkbox"
                checked={!!order.suppress_notifications}
                onChange={async (e) => {
                  const muted = e.target.checked;
                  setOrder(o => ({ ...o, suppress_notifications: muted })); // optimistic
                  try {
                    await setOrderNotificationsMuted(order.id, muted);
                    void onChanged();
                  } catch (err: any) {
                    setOrder(o => ({ ...o, suppress_notifications: !muted }));
                    alert(err?.message || 'Failed to update notifications');
                  }
                }}
                className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              <span className={`w-[76px] text-[11px] font-medium leading-tight ${order.suppress_notifications ? 'text-amber-700' : 'text-gray-500'}`}>
                No notifications
              </span>
            </label>
            {order.status !== 'cancelled' ? (
              <>
                {/* Uninvoiced orders reach "delivered"/"picked up" only through the
                    Deliver/Pickup & Invoice panel, so the money/deposit side can't
                    be skipped. Already-invoiced orders (pay-at-checkout) keep
                    "Mark delivered / picked up" — it's their only path. */}
                {advance && !(advance === 'delivered' && !hasInvoice) && (
                  <button
                    disabled={busy}
                    onClick={() => handleStatus(advance)}
                    className="flex-1 py-2.5 rounded-xl bg-gray-900 text-white text-[13px] font-semibold hover:bg-gray-800 disabled:opacity-50"
                  >
                    Mark {orderStatusLabel(advance, order.fulfillment_type).toLowerCase()}
                  </button>
                )}
                {/* The settle button only appears once the order is staged —
                    delivery: driver is out; pickup: order is ready and waiting.
                    Collect-now vs invoice-on-terms is chosen inside the panel. */}
                {!hasInvoice && order.status === 'out_for_delivery' && (
                  <button
                    disabled={busy}
                    onClick={() => {
                      void handleSavePo().then(() => {
                        setDeliverMode('deliver');
                        setShowDeliverPanel(true);
                      }).catch(() => {});
                    }}
                    className="flex-1 py-2.5 rounded-xl bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700 disabled:opacity-50"
                  >
                    {order.fulfillment_type === 'pickup' ? 'Complete pickup' : 'Deliver'}
                  </button>
                )}
                {/* Bill ahead of delivery — same invoicing, the order keeps its
                    place in the pipeline and rentals start at Mark delivered. */}
                {!hasInvoice && (order.status === 'new' || order.status === 'confirmed') && (
                  <button
                    disabled={busy}
                    onClick={() => {
                      void handleSavePo().then(() => {
                        setDeliverMode('invoice_only');
                        setShowDeliverPanel(true);
                      }).catch(() => {});
                    }}
                    className="flex-1 py-2.5 rounded-xl bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700 disabled:opacity-50"
                  >
                    Invoice now
                  </button>
                )}
                {/* Balance still owing on the invoice → collect it from here. */}
                {liveInvoice && summaryBalanceCents > 0 && (
                  <button
                    disabled={busy}
                    onClick={() => setShowRecordPayment(true)}
                    className="flex-1 py-2.5 rounded-xl bg-indigo-600 text-white text-[13px] font-semibold hover:bg-indigo-700 disabled:opacity-50"
                  >
                    Take Payment
                  </button>
                )}
              </>
            ) : (
              <button
                disabled={busy}
                onClick={() => handleStatus('new')}
                className="flex-1 py-2.5 rounded-xl bg-gray-100 text-gray-800 text-[13px] font-semibold hover:bg-gray-200 disabled:opacity-50"
              >
                Reopen order
              </button>
            )}
          </div>
        </div>

      {showAddItem && (
        <ProductPickerOverlay
          businessId={businessId}
          onPick={handleAddItem}
          onClose={() => setShowAddItem(false)}
          priceOverrides={customerPricing.prices}
          zIndex={zBase + 20}
          // Products already on the order show a stepper in the picker and
          // adjust the existing line — no duplicate rows.
          quantities={(order.items || []).reduce<Record<string, number>>((acc, i) => {
            if ((i.line_type ?? 'product') === 'product' && i.stock_item_id) {
              acc[i.stock_item_id] = (acc[i.stock_item_id] ?? 0) + i.quantity;
            }
            return acc;
          }, {})}
          onAdjust={(item, delta) => {
            const existing = (order.items || []).find(
              i => (i.line_type ?? 'product') === 'product' && i.stock_item_id === item.id
            );
            if (existing) handleQty(existing.id, existing.quantity + delta);
          }}
        />
      )}

      {showDeliverPanel && (
        <InvoiceOrderOverlay
          order={order}
          mode={deliverMode}
          zIndex={zBase + 10}
          onPoChange={(po) => {
            setOrder(o => ({ ...o, po_number: po }));
            setPoNumber(po ?? '');
          }}
          onClose={() => setShowDeliverPanel(false)}
          onDone={async (opts) => {
            setShowDeliverPanel(false);
            onInvoiced();
            if (opts?.takePayment || opts?.viewInvoice) {
              // Stay in the order: reload picks up invoice_id, then jump straight
              // into the payment modal (Take Payment) or the invoice viewer.
              await reload();
              if (opts?.takePayment) setShowRecordPayment(true);
              else setShowPayments(true);
            } else {
              onClose();
            }
          }}
        />
      )}

      <CustomerDetailsModal
        isOpen={showCustomerDetails}
        onClose={() => {
          setShowCustomerDetails(false);
          loadFullCustomer();
        }}
        customer={fullCustomer}
        onUpdate={() => { loadFullCustomer(); reload(); }}
        zIndex={zBase + 60}
      />

      {/* Invoice viewer — identical to the Invoices page standalone flow. The
          stack provider lifts it above this side panel (and the pinned-widget
          variant of it). */}
      {showPayments && order.invoice_id && (
        <ModalStackProvider zIndex={zBase + 50}>
          <BetaPaymentsModal
            isOpen={showPayments}
            onClose={() => setShowPayments(false)}
            invoiceId={order.invoice_id}
            bookingId=""
            bookingNumber={0}
            bookingData={{ total_amount: 0 }}
            onUpdate={reload}
          />
        </ModalStackProvider>
      )}

      {/* Take Payment — the standard payment modal (record / Stripe pay / tap to
          pay / reader / gift card / wallet), pointed at the order's invoice. */}
      {showRecordPayment && order.invoice_id && (
        <ModalStackProvider zIndex={zBase + 50}>
          <RecordPaymentModal
            isOpen={showRecordPayment}
            onClose={() => setShowRecordPayment(false)}
            onSuccess={async () => { await reload(); onChanged(); }}
            onSendInvoice={() => { setShowRecordPayment(false); setShowPayments(true); }}
            invoiceId={order.invoice_id}
            productSaleId={order.product_sale_id || undefined}
            businessId={businessId}
            bookingTotal={order.invoice ? Number(order.invoice.total_cents) / 100 : order.estimated_total_cents / 100}
            amountPaid={order.invoice ? Number(order.invoice.amount_paid_cents) / 100 : 0}
            customerId={order.customer_id || undefined}
          />
        </ModalStackProvider>
      )}
    </SidePanel>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Order timeline — Shopify-style event history, newest first, grouped by day.
// ─────────────────────────────────────────────────────────────────────────────

const EVENT_DOT_STYLES: Record<string, string> = {
  created: 'bg-blue-500',
  status: 'bg-gray-400',
  invoiced: 'bg-indigo-500',
  billing_reopened: 'bg-amber-500',
  payment: 'bg-green-500',
  email: 'bg-sky-400',
  items: 'bg-gray-300',
  fulfillment: 'bg-gray-400',
};

function timelineDayLabel(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOf(now) - startOf(d)) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    ...(d.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  });
}

function OrderTimeline({
  events,
  order,
  staffNames,
}: {
  events: OrderEvent[];
  order: CustomerOrder;
  staffNames: Record<string, string>;
}) {
  // Orders that predate the events table still get their placed entry.
  const display = useMemo(() => {
    const evs = [...events];
    if (!evs.some(e => e.event_type === 'created')) {
      evs.push({
        id: `synthetic-${order.id}`,
        order_id: order.id,
        event_type: 'created',
        message: order.source === 'staff' ? 'Order created by staff' : `Order placed online (${order.source})`,
        details: null,
        actor_id: null,
        created_at: order.placed_at,
      });
    }
    return evs.sort((a, b) => b.created_at.localeCompare(a.created_at));
  }, [events, order]);

  const groups = useMemo(() => {
    const out: Array<{ label: string; events: OrderEvent[] }> = [];
    for (const e of display) {
      const label = timelineDayLabel(e.created_at);
      const last = out[out.length - 1];
      if (last && last.label === label) last.events.push(e);
      else out.push({ label, events: [e] });
    }
    return out;
  }, [display]);

  return (
    <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] p-4">
      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Timeline</h4>
      <div className="space-y-4">
        {groups.map(group => (
          <div key={group.label}>
            <p className="text-[11px] font-semibold text-gray-400 mb-2">{group.label}</p>
            <div className="relative ml-1 pl-4 border-l-2 border-gray-100 space-y-3">
              {group.events.map(e => (
                <div key={e.id} className="relative flex items-start justify-between gap-3">
                  <span
                    className={`absolute -left-[23px] top-[5px] w-2.5 h-2.5 rounded-full ring-2 ring-white ${
                      EVENT_DOT_STYLES[e.event_type] || 'bg-gray-300'
                    }`}
                  />
                  <p className="text-[12.5px] text-gray-700 leading-snug">
                    {e.message}
                    {e.actor_id && staffNames[e.actor_id] && (
                      <span className="text-gray-400"> — {staffNames[e.actor_id]}</span>
                    )}
                  </p>
                  <span className="text-[11px] text-gray-400 flex-shrink-0 pt-0.5">
                    {new Date(e.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Deliver & Invoice
// ─────────────────────────────────────────────────────────────────────────────

/** Proof-of-delivery photos: camera-first capture (like the business card
 *  scanner) with an upload fallback. Uploads persist on the order immediately,
 *  so photos survive even if the deliver flow is abandoned. */
function DeliveryPhotosSection({ order, editable = true, compact = false }: {
  order: CustomerOrder;
  editable?: boolean;
  compact?: boolean;
}) {
  const [photos, setPhotos] = useState<DeliveryPhoto[]>(order.delivery_photos || []);
  const [showCamera, setShowCamera] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { open: openImageViewer } = useImageViewer();
  const pickup = order.fulfillment_type === 'pickup';

  // Re-seed when the order re-fetches (uploads persist immediately, so the
  // server list is always at least as fresh as local state).
  useEffect(() => { setPhotos(order.delivery_photos || []); }, [order.id, order.delivery_photos]);

  const persist = async (next: DeliveryPhoto[]) => {
    setPhotos(next);
    try { await saveDeliveryPhotos(order.id, next); } catch (err) {
      console.error('Failed to save delivery photos:', err);
    }
  };

  const addFiles = async (files: File[]) => {
    if (files.length === 0) return;
    setUploading(true);
    try {
      const uploaded: DeliveryPhoto[] = [];
      for (const file of files) {
        try {
          uploaded.push(await uploadDeliveryPhoto(order.id, file));
        } catch (err) {
          console.error('Delivery photo upload failed:', err);
          alert('Photo upload failed — please try again.');
        }
      }
      if (uploaded.length > 0) await persist([...photos, ...uploaded]);
    } finally {
      setUploading(false);
    }
  };

  const handleCameraPhotos = async (blobUrls: string[]) => {
    setShowCamera(false);
    const files: File[] = [];
    for (let i = 0; i < blobUrls.length; i++) {
      try {
        files.push(await blobUrlToFile(blobUrls[i], `delivery-${Date.now()}-${i + 1}.jpg`));
      } finally {
        URL.revokeObjectURL(blobUrls[i]);
      }
    }
    await addFiles(files);
  };

  const removePhoto = async (index: number) => {
    const photo = photos[index];
    await persist(photos.filter((_, i) => i !== index));
    deleteDeliveryPhotoFile(photo.path).catch(() => {});
  };

  if (!editable && photos.length === 0) return null;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-[13px] font-semibold text-gray-900 flex items-center gap-1.5">
          <Camera className="w-4 h-4 text-gray-400" /> {pickup ? 'Pickup photos' : 'Delivery photos'}
          {photos.length > 0 && <span className="text-[11px] font-medium text-gray-400">({photos.length})</span>}
        </h4>
        {editable && (
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => setShowCamera(true)}
              disabled={uploading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-900 text-white text-[11px] font-semibold hover:bg-gray-800 disabled:opacity-50"
            >
              <Camera className="w-3.5 h-3.5" /> {uploading ? 'Uploading…' : 'Take photo'}
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="px-3 py-1.5 rounded-full border border-gray-200 text-[11px] font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            >
              Upload
            </button>
          </div>
        )}
      </div>
      {photos.length > 0 ? (
        <div className="grid grid-cols-4 gap-2">
          {photos.map((p, i) => (
            <div key={p.path} className="relative group">
              <img
                src={p.url}
                alt={`Delivery photo ${i + 1}`}
                onClick={() => openImageViewer({
                  images: photos.map(ph => ({ src: ph.url, caption: `Order ${order.order_number}` })),
                  index: i,
                })}
                className="w-full aspect-square object-cover rounded-lg border border-gray-200 cursor-pointer"
                loading="lazy"
              />
              {editable && (
                <button
                  type="button"
                  onClick={() => removePhoto(i)}
                  className="absolute -top-1.5 -right-1.5 p-1 bg-red-500 text-white rounded-full shadow opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      ) : editable && !compact ? (
        <p className="text-[11px] text-gray-400">
          {pickup
            ? 'Snap a photo of the handoff if you want one on record — it\'s saved on the order.'
            : 'Snap a photo of where the delivery was left — it\'s saved on the order.'}
        </p>
      ) : null}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={(e) => {
          addFiles(Array.from(e.target.files || []));
          e.target.value = '';
        }}
        className="hidden"
      />
      <CameraCapture
        isOpen={showCamera}
        onClose={() => setShowCamera(false)}
        onPhotosCapture={handleCameraPhotos}
        title={`${pickup ? 'Pickup' : 'Delivery'} photos — order ${order.order_number}`}
      />
    </div>
  );
}

function InvoiceOrderOverlay({
  order,
  onClose,
  onDone,
  onPoChange,
  zIndex = 100070,
  mode = 'deliver',
}: {
  order: CustomerOrder;
  onClose: () => void;
  /** takePayment → open the Take Payment modal; viewInvoice → open the invoice viewer. */
  onDone: (opts?: { takePayment?: boolean; viewInvoice?: boolean }) => void;
  /** PO typed here, saved onto the order before the invoice is issued. */
  onPoChange?: (po: string | null) => void;
  zIndex?: number;
  /** 'deliver' marks the order delivered; 'invoice_only' bills it and leaves it in the pipeline. */
  mode?: 'deliver' | 'invoice_only';
}) {
  const invoiceOnly = mode === 'invoice_only';
  const [quote, setQuote] = useState<OrderInvoiceQuote | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{
    invoiceNumber: string;
    total: number;
    invoiceId: string;
    saleId: string;
    walletCredit: number;
    /** Deposit refund taken as a credit line on this invoice, dollars. */
    invoiceCredit: number;
    /** Deposit refund handed back externally (cash / e-transfer), dollars. */
    externalRefund: number;
    /** Deposit refund that went back to a card, dollars. */
    cardRefund: number;
    /** Wallet credit auto-applied to this invoice (wallet-first), dollars. */
    walletApplied: number;
    /** Remaining balance after wallet application, dollars. */
    balanceDue: number;
    termsApplied: string;
    /** Recipient the invoice email went to; null = not sent (no email / failed). */
    emailedTo: string | null;
    emailError: string | null;
    payLinkIncluded: boolean;
  } | null>(null);
  const [payLink, setPayLink] = useState<string | null>(null);
  const [linkBusy, setLinkBusy] = useState(false);

  // How the money side settles: collect now (payment modal) or issue the
  // invoice open on terms. Chosen inside this panel — one Deliver entry point.
  const [settle, setSettle] = useState<'pay' | 'invoice'>('pay');
  const settleTouched = useRef(false);

  // Invoice terms: 'default' = whatever the customer/business default resolves to.
  // Staff can override per invoice (one-off Net 30, custom date, ...).
  const [terms, setTerms] = useState<InvoiceTermCode | 'default'>('default');
  const [customDue, setCustomDue] = useState('');
  const [defaultTerm, setDefaultTerm] = useState<{ term: InvoiceTermCode; source: 'customer' | 'business' }>({
    term: 'due_on_receipt',
    source: 'business',
  });
  // Wallet on file — auto-applied to due-on-receipt invoices (wallet-first).
  const [customerWallet, setCustomerWallet] = useState(0);
  // Where an excess-return deposit refund goes: wallet credit (default), a
  // credit line on this invoice, back to a card they paid with, or cash /
  // e-transfer — all through the shared refund engine.
  // Auto-picked: wallet credit for customers, cash for guest orders.
  const [refundMode, setRefundMode] = useState<ExcessRefundMode>(order.customer_id ? 'wallet' : 'cash');
  const [refundReference, setRefundReference] = useState('');
  // Starts with the PO from the order, if one was entered earlier. Blank means
  // none yet — staff can add it here, and it lands on the invoice.
  const [poNumber, setPoNumber] = useState(order.po_number ?? '');

  useEffect(() => {
    (async () => {
      try {
        if (order.customer_id) {
          const { data } = await supabase
            .from('customers')
            .select('default_payment_terms, wallet')
            .eq('id', order.customer_id)
            .maybeSingle();
          setCustomerWallet(Number(data?.wallet || 0));
          if (data?.default_payment_terms) {
            setDefaultTerm({ term: data.default_payment_terms as InvoiceTermCode, source: 'customer' });
            // Terms customers (Net 30 etc.) almost always get invoiced, so
            // preselect that — staff can still flip to collect on the spot.
            if (!settleTouched.current) setSettle('invoice');
            return;
          }
        }
        const { data: settings } = await supabase
          .from('invoice_settings')
          .select('default_payment_terms')
          .eq('business_id', order.business_id)
          .maybeSingle();
        if (settings?.default_payment_terms && TERM_LABELS[settings.default_payment_terms]) {
          setDefaultTerm({ term: settings.default_payment_terms as InvoiceTermCode, source: 'business' });
        }
      } catch (err) {
        console.error('Failed to load default payment terms:', err);
      }
    })();
  }, [order.customer_id, order.business_id]);

  // Empties collected at delivery, editable per deposit-bearing product.
  // null = not initialized yet (server defaults to the checkout swap assumption).
  const [returns, setReturns] = useState<Record<string, { returned: string; lost: string }> | null>(null);

  const returnsPayload = (r: Record<string, { returned: string; lost: string }> | null): OrderReturnInput[] | null =>
    r
      ? Object.entries(r).map(([stock_item_id, v]) => ({
          stock_item_id,
          returned: Math.max(0, parseInt(v.returned) || 0),
          lost: Math.max(0, parseInt(v.lost) || 0),
        }))
      : null;

  useEffect(() => {
    let cancelled = false;
    // Debounced so typing in the returns inputs doesn't fire an RPC per key.
    const timer = setTimeout(async () => {
      try {
        const q = await quoteCustomerOrderInvoice(order.id, returnsPayload(returns));
        if (cancelled) return;
        setQuote(q);
        // First quote establishes the editable defaults from the server
        // (the checkout swap assumption, when the customer declared empties).
        if (returns === null && q.deposits.length > 0) {
          setReturns(Object.fromEntries(q.deposits.map(d => [
            d.stock_item_id,
            { returned: String(d.returned), lost: String(d.lost) },
          ])));
        }
      } catch (err) {
        console.error('Failed to quote order:', err);
      }
    }, returns === null ? 0 : 350);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, returns]);

  const total = quote ? quote.totalCents / 100 : order.estimated_total_cents / 100;
  const depositCharged = quote ? quote.deposits.reduce((n, d) => n + d.net_new * d.deposit_cents, 0) : 0;
  const walletRefund = quote ? quote.deposits.reduce((n, d) => n + d.excess_refund_cents, 0) : 0;

  // The effective term when "default" is selected, and the label shown for it.
  const effectiveTerm: InvoiceTermCode = terms === 'default' ? defaultTerm.term : terms;
  const dueDatePreview =
    effectiveTerm === 'custom'
      ? customDue
        ? new Date(`${customDue}T00:00:00`).toLocaleDateString()
        : null
      : effectiveTerm === 'due_on_receipt'
      ? null
      : computeDueDate(new Date(), effectiveTerm)
        ? new Date(computeDueDate(new Date(), effectiveTerm) as string).toLocaleDateString()
        : null;

  const termsReady = effectiveTerm !== 'custom' || !!customDue;

  // Send the issued invoice the same way booking invoices go out: booking-style
  // email, PDF attached (generate-invoice-pdf), and a secure payment link when
  // the business has Stripe connected. Logged to invoice_history (send history).
  const autoSendInvoiceEmail = async (args: {
    invoiceId: string;
    saleId: string;
    invoiceNumber: string;
    totalCents: number;
    /** Owed after wallet-first application — the payment link collects this. */
    balanceDueCents: number;
    walletAppliedCents: number;
    /** Net/custom terms: not due yet, so no payment link is sent. */
    termed: boolean;
    termsApplied: string;
  }): Promise<{ emailedTo: string | null; payLinkIncluded: boolean; error: string | null }> => {
    const [{ data: biz }, { data: inv }] = await Promise.all([
      supabase
        .from('businesses')
        .select('name, stripe_connect_account_id, online_payments_enabled')
        .eq('id', order.business_id)
        .single(),
      supabase
        .from('invoices')
        .select('bill_to_email, bill_to_name')
        .eq('id', args.invoiceId)
        .single(),
    ]);

    const to = (inv?.bill_to_email || order.customer?.email || order.contact_email || '').trim();
    if (!to) return { emailedTo: null, payLinkIncluded: false, error: 'no email on file for this customer' };

    const businessName = biz?.name || 'Business';
    const totalStr = `$${(args.totalCents / 100).toFixed(2)}`;
    const balanceStr = `$${(args.balanceDueCents / 100).toFixed(2)}`;

    // Payment link — only when something is actually collectible now: the
    // business takes online payments, a balance remains after wallet credit,
    // and the invoice is due on receipt (net-terms invoices aren't due yet).
    let paymentLinkHtml = '';
    let payLinkIncluded = false;
    let paymentUrl: string | null = null;
    if (
      biz?.stripe_connect_account_id &&
      biz.online_payments_enabled !== false &&
      args.balanceDueCents > 0 &&
      !args.termed
    ) {
      const token = crypto.randomUUID();
      const { error: payErr } = await supabase.from('payments').insert({
        business_id: order.business_id,
        booking_id: null,
        product_sale_id: args.saleId,
        invoice_id: args.invoiceId,
        amount: args.balanceDueCents / 100,
        method: 'credit_card',
        status: 'pending',
        reference: `Order ${order.order_number}`,
        notes: `Payment link for order ${order.order_number}`,
        payment_token: token,
      });
      if (!payErr) {
        paymentUrl = `${window.location.origin}/pay-invoice/${token}`;
        setPayLink(paymentUrl);
        payLinkIncluded = true;
        // Same button block the booking invoice emails use.
        paymentLinkHtml = `
          <div style="margin: 32px 0; padding: 24px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 12px; text-align: center;">
            <p style="color: #6b7280; margin: 0 0 20px 0; font-size: 15px; line-height: 1.5;">Pay your invoice securely online</p>
            <a href="${paymentUrl}"
               style="display: inline-block; background: #007AFF; color: #ffffff; padding: 14px 32px; text-decoration: none; border-radius: 10px; font-weight: 500; font-size: 16px; letter-spacing: -0.01em;">
              Pay ${balanceStr}
            </a>
            <p style="color: #9ca3af; margin: 16px 0 0 0; font-size: 12px; line-height: 1.4;">Secure payment processed by Stripe</p>
          </div>
        `;
      }
    }

    // Invoice PDF — same server-side generator the booking send uses.
    let attachments: Array<{ filename: string; content: string; type: string }> | undefined;
    try {
      const { data: pdf } = await supabase.functions.invoke('generate-invoice-pdf', {
        body: { invoiceId: args.invoiceId },
      });
      if (pdf?.base64) {
        attachments = [{
          filename: pdf.filename || `invoice-${args.invoiceNumber}.pdf`,
          content: pdf.base64,
          type: 'application/pdf',
        }];
      }
    } catch (e) {
      console.error('Invoice PDF generation failed (sending without attachment):', e);
    }

    const firstName =
      order.customer?.first_name || (order.contact_name || '').split(' ')[0] || 'there';
    const walletStr = `$${(args.walletAppliedCents / 100).toFixed(2)}`;
    const messageLines = [
      `Hi ${firstName},`,
      '',
      `Thank you for your order ${order.order_number} from ${businessName}.`,
      args.walletAppliedCents > 0 && args.balanceDueCents <= 0
        ? `Invoice #${args.invoiceNumber} for ${totalStr} is attached — it was fully paid from your wallet balance (${walletStr}). Nothing is owed.`
        : args.walletAppliedCents > 0
        ? `Invoice #${args.invoiceNumber} for ${totalStr} is attached — ${walletStr} was paid from your wallet balance and the remaining ${balanceStr} is ${args.termsApplied}.`
        : `Invoice #${args.invoiceNumber} for ${totalStr} is attached — payment is ${args.termsApplied}.`,
    ];
    const messageHtml = messageLines
      .map(line => `<p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0 0 8px 0;">${line || '&nbsp;'}</p>`)
      .join('\n');

    const emailBody = `
      <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; max-width: 600px; margin: 0 auto; background: #ffffff;">
        <div style="padding: 40px 32px 32px 32px;">
          <div style="margin-bottom: 32px;">
            <h1 style="color: #000000; margin: 0 0 8px 0; font-size: 28px; font-weight: 600; letter-spacing: -0.02em; line-height: 1.2;">Invoice #${args.invoiceNumber}</h1>
            <p style="color: #6b7280; margin: 0; font-size: 15px; line-height: 1.5;">${businessName}</p>
          </div>
          ${messageHtml}
          ${paymentLinkHtml}
        </div>
        <div style="padding: 24px 32px; background: #f9fafb; border-top: 1px solid #e5e7eb;">
          <p style="color: #9ca3af; font-size: 13px; margin: 0; text-align: center; line-height: 1.5;">This is an automated invoice from ${businessName}</p>
        </div>
      </div>
    `;

    const sendRes = await sendEmailWithFallback({
      businessId: order.business_id,
      to,
      subject: `Invoice #${args.invoiceNumber} from ${businessName}${
        args.balanceDueCents <= 0 ? ' - Paid' : payLinkIncluded ? ' - Pay Online' : ''
      }`,
      body: emailBody,
      attachments,
      customerId: order.customer_id || undefined,
    });
    if (!sendRes.success) {
      return { emailedTo: null, payLinkIncluded, error: sendRes.error || 'email delivery failed' };
    }

    // Send history — shows up in the invoice viewer like booking sends.
    try {
      await supabase.from('invoice_history').insert({
        business_id: order.business_id,
        invoice_id: args.invoiceId,
        sent_via: 'email',
        has_payment_link: payLinkIncluded,
        payment_link_url: paymentUrl,
        customer_email: to,
        customer_name: inv?.bill_to_name || order.contact_name || null,
      });
    } catch (e) {
      console.error('Failed to record invoice send history:', e);
    }

    return { emailedTo: to, payLinkIncluded, error: null };
  };

  const handleInvoice = async (mode: 'pay' | 'invoice') => {
    setBusy(true);
    try {
      const nextPo = poNumber.trim();
      if (nextPo !== (order.po_number ?? '').trim()) {
        await updateOrderPoNumber(order.id, nextPo);
        onPoChange?.(nextPo || null);
      }
      const res = await invoiceCustomerOrder(
        order.id,
        null, // payment is collected via the standard Take Payment modal
        // The quoted total doesn't include an invoice-credit line, so skip the
        // expected-total guard when the refund is taken on the invoice.
        refundMode === 'invoice' ? undefined : quote ? quote.totalCents : undefined,
        returnsPayload(returns),
        effectiveTerm === 'custom'
          ? { dueAt: new Date(`${customDue}T00:00:00`).toISOString() }
          : { paymentTerms: effectiveTerm },
        refundMode,
        !invoiceOnly
      );
      if (mode === 'pay') {
        // Straight into the Take Payment flow — same as booking payments.
        onDone({ takePayment: true });
        return;
      }

      const termsApplied =
        effectiveTerm === 'custom'
          ? `due ${new Date(`${customDue}T00:00:00`).toLocaleDateString()}`
          : effectiveTerm === 'due_on_receipt'
          ? 'due on receipt'
          : `${termLabel(effectiveTerm)} — due ${dueDatePreview}`;

      const walletAppliedCents = res.wallet_applied_cents ?? 0;
      const balanceDueCents = Math.max(res.total_cents - walletAppliedCents, 0);

      // Send the invoice email right away — the success screen reports where it
      // went. Muted orders skip the send entirely (invoice still exists; staff
      // can email it later from the invoice viewer after unmuting).
      let emailedTo: string | null = null;
      let emailError: string | null = null;
      let payLinkIncluded = false;
      if (order.suppress_notifications) {
        emailError = 'notifications are muted for this order';
      } else try {
        const send = await autoSendInvoiceEmail({
          invoiceId: res.invoice_id,
          saleId: res.sale_id,
          invoiceNumber: res.invoice_number,
          totalCents: res.total_cents,
          balanceDueCents,
          walletAppliedCents,
          termed: effectiveTerm !== 'due_on_receipt',
          termsApplied,
        });
        emailedTo = send.emailedTo;
        emailError = send.error;
        payLinkIncluded = send.payLinkIncluded;
      } catch (e: any) {
        emailError = e?.message || 'email delivery failed';
      }

      setResult({
        invoiceNumber: res.invoice_number,
        total: res.total_cents / 100,
        invoiceId: res.invoice_id,
        saleId: res.sale_id,
        walletCredit: (res.wallet_credit_cents ?? 0) / 100,
        invoiceCredit: (res.invoice_credit_cents ?? 0) / 100,
        externalRefund: (res.external_refund_cents ?? 0) / 100,
        cardRefund: (res.card_refund_cents ?? 0) / 100,
        walletApplied: walletAppliedCents / 100,
        balanceDue: balanceDueCents / 100,
        termsApplied,
        emailedTo,
        emailError,
        payLinkIncluded,
      });
    } catch (err: any) {
      alert(err?.message || 'Failed to invoice order');
    } finally {
      setBusy(false);
    }
  };

  const handleCopyPayLink = async () => {
    if (!result) return;
    setLinkBusy(true);
    try {
      let link = payLink;
      if (!link) {
        const token = crypto.randomUUID();
        const { error } = await supabase.from('payments').insert({
          business_id: order.business_id,
          booking_id: null,
          product_sale_id: result.saleId,
          invoice_id: result.invoiceId,
          amount: result.balanceDue,
          method: 'credit_card',
          status: 'pending',
          reference: `Order ${order.order_number}`,
          notes: `Payment link for order ${order.order_number}`,
          payment_token: token,
        });
        if (error) throw error;
        link = `${window.location.origin}/pay-invoice/${token}`;
        setPayLink(link);
      }
      await navigator.clipboard.writeText(link);
      alert('Payment link copied to clipboard');
    } catch (err: any) {
      alert(err?.message || 'Failed to create payment link');
    } finally {
      setLinkBusy(false);
    }
  };

  return (
    <SidePanel onClose={onClose} width="md:w-[480px]" zIndex={zIndex}>
        {result ? (
          <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-sm text-center space-y-3">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
            <h3 className="text-[16px] font-bold text-gray-900">Invoice {result.invoiceNumber} issued</h3>
            <p className="text-[13px] text-gray-600">
              {result.balanceDue <= 0
                ? `${fmtMoney(Math.round(result.total * 100))} — fully paid. Stock has been deducted.`
                : `${fmtMoney(Math.round(result.total * 100))} ${result.termsApplied}. Stock has been deducted.`}
            </p>
            {result.walletApplied > 0 && (
              <p className="text-[13px] text-emerald-600">
                {fmtMoney(Math.round(result.walletApplied * 100))} paid from the customer's wallet balance
                {result.balanceDue > 0 ? ` — ${fmtMoney(Math.round(result.balanceDue * 100))} remaining.` : '.'}
              </p>
            )}
            {result.walletCredit > 0 && (
              <p className="text-[13px] text-emerald-600">
                {fmtMoney(Math.round(result.walletCredit * 100))} excess container deposit credited to the customer's wallet.
              </p>
            )}
            {result.invoiceCredit > 0 && (
              <p className="text-[13px] text-emerald-600">
                {fmtMoney(Math.round(result.invoiceCredit * 100))} container deposit refund credited on this invoice.
              </p>
            )}
            {result.externalRefund > 0 && (
              <p className="text-[13px] text-amber-700">
                Hand {fmtMoney(Math.round(result.externalRefund * 100))} back to the customer (deposit refund recorded as paid out in {refundMode === 'e_transfer' ? 'e-transfer' : 'cash'}).
              </p>
            )}
            {result.cardRefund > 0 && (
              <p className="text-[13px] text-emerald-600">
                {fmtMoney(Math.round(result.cardRefund * 100))} container deposit refunded back to the customer's card.
              </p>
            )}
            {result.emailedTo ? (
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3.5 text-left space-y-0.5">
                <p className="text-[13px] font-semibold text-emerald-800 flex items-center gap-1.5">
                  <Mail className="w-4 h-4" /> Invoice emailed
                </p>
                <p className="text-[12px] text-emerald-700">
                  Sent to <span className="font-semibold">{result.emailedTo}</span> with the invoice PDF attached
                  {result.payLinkIncluded ? ' and a secure payment link' : ''}.
                </p>
              </div>
            ) : (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3.5 text-left space-y-2">
                <p className="text-[12px] text-amber-800">
                  Couldn't email the invoice — {result.emailError || 'sending failed'}.{' '}
                  {result.balanceDue > 0
                    ? 'Copy the payment link below or send it from the invoice viewer.'
                    : 'Nothing is owed on it — you can resend it from the invoice viewer.'}
                </p>
                {result.balanceDue > 0 && (
                  <button
                    disabled={linkBusy}
                    onClick={handleCopyPayLink}
                    className="w-full py-2 rounded-lg bg-gray-900 text-white text-[12px] font-semibold hover:bg-gray-800 disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                  >
                    {linkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LinkIcon className="w-4 h-4" />} Copy payment link
                  </button>
                )}
              </div>
            )}
            <button onClick={() => onDone()} className="w-full py-2.5 rounded-xl bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700">
              Done
            </button>
            <button
              onClick={() => onDone({ viewInvoice: true })}
              className="w-full py-1.5 text-[12px] font-semibold text-gray-500 hover:text-gray-700"
            >
              View invoice →
            </button>
          </div>
          </div>
        ) : (
          <>
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="text-[15px] font-bold text-gray-900">
                  {invoiceOnly ? 'Invoice now' : order.fulfillment_type === 'pickup' ? 'Complete pickup' : 'Deliver order'}
                </h3>
                {invoiceOnly && (
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    Bills the order today; it stays {orderStatusLabel(order.status, order.fulfillment_type).toLowerCase()} until you mark it {order.fulfillment_type === 'pickup' ? 'picked up' : 'delivered'}.
                  </p>
                )}
              </div>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              <div>
                <label htmlFor="invoice-order-po" className="flex items-center justify-between text-[13px] font-semibold text-gray-900 mb-2">
                  PO number
                  <span className="text-[11px] font-medium text-gray-400">Optional</span>
                </label>
                <input
                  id="invoice-order-po"
                  value={poNumber}
                  onChange={(e) => setPoNumber(e.target.value)}
                  onBlur={() => {
                    const next = poNumber.trim();
                    if (next === (order.po_number ?? '').trim()) return;
                    void updateOrderPoNumber(order.id, next)
                      .then(() => onPoChange?.(next || null))
                      .catch((err: any) => alert(err?.message || 'Failed to save PO number'));
                  }}
                  placeholder="No PO"
                  autoComplete="off"
                  className="w-full px-3 py-2 text-[13px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                />
              </div>
              {/* How to settle — the old two footer buttons, now one choice here */}
              <div>
                <h4 className="text-[13px] font-semibold text-gray-900 mb-2">Payment</h4>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => { settleTouched.current = true; setSettle('pay'); }}
                    className={`rounded-xl border p-3 text-left transition ${
                      settle === 'pay'
                        ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500'
                        : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <span className={`block text-[13px] font-semibold ${settle === 'pay' ? 'text-indigo-700' : 'text-gray-900'}`}>
                      Take payment
                    </span>
                    <span className="block text-[11px] text-gray-500 mt-0.5">
                      Collect now — cash, card, tap to pay…
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => { settleTouched.current = true; setSettle('invoice'); }}
                    className={`rounded-xl border p-3 text-left transition ${
                      settle === 'invoice'
                        ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                        : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <span className={`block text-[13px] font-semibold ${settle === 'invoice' ? 'text-blue-700' : 'text-gray-900'}`}>
                      Send invoice
                    </span>
                    <span className="block text-[11px] text-gray-500 mt-0.5">
                      Invoice on terms ({termLabel(defaultTerm.term)})
                    </span>
                  </button>
                </div>
              </div>
              {/* Container deposits: enter the empties the driver actually collected */}
              {returns && quote && quote.deposits.length > 0 && (
                <div>
                  <h4 className="text-[13px] font-semibold text-gray-900 mb-1">Container deposits</h4>
                  <p className="text-[11px] text-gray-500 mb-2">
                    {invoiceOnly
                      ? `Empties expected back at ${order.fulfillment_type === 'pickup' ? 'pickup' : 'delivery'} — they are billed now. If the count differs at the door, use Reopen billing on the order and invoice again.`
                      : `Enter the empties collected at ${order.fulfillment_type === 'pickup' ? 'pickup' : 'delivery'}. Deposit is only charged on containers the customer keeps.`}
                  </p>
                  <div className="space-y-2">
                    {quote.deposits.map(d => {
                      const onHand = d.prior_balance ?? 0;
                      const setBoth = (returned: number, lost: number) =>
                        setReturns(prev => ({
                          ...(prev || {}),
                          [d.stock_item_id]: { returned: String(returned), lost: String(lost) },
                        }));
                      return (
                      <div key={d.stock_item_id} className="rounded-xl border border-gray-200 p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-[13px] font-medium text-gray-900">{d.product_name}</span>
                          <span className="text-[11px] text-gray-500">
                            {d.delivered} delivered · {fmtMoney(d.deposit_cents)}/deposit
                          </span>
                        </div>
                        <p className={`text-[12px] font-semibold ${onHand > 0 ? 'text-blue-700' : 'text-gray-500'}`}>
                          Customer has {onHand} on record{onHand > 0 ? ' — collect the empties' : ' — every empty picked up still refunds'}
                        </p>
                        {onHand > 0 && (
                          <div className="flex gap-1.5">
                            <button
                              type="button"
                              onClick={() => setBoth(Math.min(d.delivered, onHand), 0)}
                              className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"
                            >
                              Full swap ({Math.min(d.delivered, onHand)} back)
                            </button>
                            <button
                              type="button"
                              onClick={() => setBoth(onHand, 0)}
                              className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                            >
                              All {onHand} back
                            </button>
                            <button
                              type="button"
                              onClick={() => setBoth(0, 0)}
                              className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                            >
                              None
                            </button>
                          </div>
                        )}
                        <div className="flex items-center gap-3">
                          <label className="flex-1">
                            <span className="block text-[11px] text-gray-500 mb-1">Empties returned</span>
                            <input
                              type="number"
                              min="0"
                              value={returns[d.stock_item_id]?.returned ?? '0'}
                              onChange={(e) => setReturns(prev => ({
                                ...(prev || {}),
                                [d.stock_item_id]: { returned: e.target.value, lost: prev?.[d.stock_item_id]?.lost ?? '0' },
                              }))}
                              className="w-full px-3 py-2 text-[13px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                            />
                          </label>
                          <label className="flex-1">
                            <span className="block text-[11px] text-gray-500 mb-1">Lost (deposit kept)</span>
                            <input
                              type="number"
                              min="0"
                              max={onHand}
                              value={returns[d.stock_item_id]?.lost ?? '0'}
                              onChange={(e) => setReturns(prev => ({
                                ...(prev || {}),
                                [d.stock_item_id]: { returned: prev?.[d.stock_item_id]?.returned ?? '0', lost: e.target.value },
                              }))}
                              className="w-full px-3 py-2 text-[13px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                            />
                          </label>
                        </div>
                        <p className="text-[11px] text-gray-500">
                          {d.net_new > 0
                            ? `${d.net_new} new deposit${d.net_new === 1 ? '' : 's'} charged (${fmtMoney(d.net_new * d.deposit_cents)})`
                            : d.excess_refund_cents > 0
                            ? `${fmtMoney(d.excess_refund_cents)} deposit refund due`
                            : 'Even swap — no new deposit'}
                          {(() => {
                            const l = Math.max(0, parseInt(returns[d.stock_item_id]?.lost ?? '0') || 0);
                            const held = Math.max(onHand, 0);
                            if (l > held) return ` · lost capped at the ${held} deposit${held === 1 ? '' : 's'} on record`;
                            return '';
                          })()}
                        </p>
                        {(() => {
                          // Pickups beyond the recorded balance are legacy jugs: they
                          // refund at today's rate and the count is corrected.
                          const r = Math.max(0, parseInt(returns[d.stock_item_id]?.returned ?? '0') || 0);
                          const unmatched = d.unmatched ?? 0;
                          if (unmatched <= 0) return null;
                          return (
                            <p className="text-[11px] font-medium text-amber-700">
                              {unmatched} of the {r} picked up {unmatched === 1 ? 'isn\u2019t' : 'aren\u2019t'} on this customer&apos;s record — refunded at today&apos;s {fmtMoney(d.unmatched_rate_cents ?? d.deposit_cents)} rate and the count is corrected.
                            </p>
                          );
                        })()}
                      </div>
                      );
                    })}
                  </div>

                </div>
              )}

              {/* Proof-of-delivery photos — camera-first, saved on the order */}
              {!invoiceOnly && <DeliveryPhotosSection order={order} />}

              <div className="bg-gray-50 rounded-xl p-3.5 space-y-1">
                {quote ? (
                  <>
                    <Row label="Subtotal" value={`$${(quote.subtotalCents / 100).toFixed(2)}`} />
                    {quote.discountCents > 0 && <Row label="Discount" value={`-$${(quote.discountCents / 100).toFixed(2)}`} accent="text-green-600" />}
                    <Row label="Tax" value={`$${(quote.taxCents / 100).toFixed(2)}`} />
                    <div className="border-t border-gray-200 pt-1.5 mt-1.5">
                      <Row label="Total" value={`$${(quote.totalCents / 100).toFixed(2)}`} bold />
                    </div>
                    {depositCharged > 0 && (
                      <p className="text-[11px] text-gray-500 pt-0.5">
                        Includes {fmtMoney(depositCharged)} refundable container deposit (not taxed).
                      </p>
                    )}
                    {/* Deposit refund — pre-chosen (wallet for customers, cash for
                        guests); the pill drops down to change it. */}
                    {walletRefund > 0 && (
                      <div className="mt-1.5 rounded-lg border border-emerald-200/70 bg-emerald-50/40 px-2.5 py-1.5">
                        <RefundDestinationSelect
                          businessId={order.business_id}
                          customerId={order.customer_id}
                          amountCents={walletRefund}
                          value={refundMode === 'external' ? 'cash' : refundMode}
                          onChange={next => setRefundMode(next as ExcessRefundMode)}
                          destinations={order.customer_id
                            ? ['wallet', 'card', 'cash', 'e_transfer', 'invoice']
                            : ['cash', 'e_transfer', 'invoice', 'card', 'wallet']}
                          reference={refundReference}
                          onReferenceChange={setRefundReference}
                          direction="down"
                        />
                        {refundMode === 'invoice' && (
                          <p className="mt-0.5 text-[11px] text-gray-500">
                            Shown as a credit line — the final invoice total will be about {fmtMoney(Math.max(quote.totalCents - walletRefund, 0))}.
                          </p>
                        )}
                      </div>
                    )}
                    {(() => {
                      // Wallet-first preview: refunds credited this visit also count.
                      const availableCents =
                        Math.round(customerWallet * 100) + (refundMode === 'wallet' ? walletRefund : 0);
                      const willApply = Math.min(availableCents, quote.totalCents);
                      if (willApply <= 0 || effectiveTerm !== 'due_on_receipt') return null;
                      return (
                        <p className="text-[11px] text-emerald-600 pt-0.5">
                          Wallet credit of {fmtMoney(willApply)} will be applied automatically
                          {willApply >= quote.totalCents
                            ? ' — the invoice will be fully paid.'
                            : ` — ${fmtMoney(quote.totalCents - willApply)} left to collect.`}
                        </p>
                      );
                    })()}
                  </>
                ) : (
                  <div className="flex items-center gap-2 text-[13px] text-gray-500 py-1">
                    <Loader2 className="w-4 h-4 animate-spin" /> Calculating exact total…
                  </div>
                )}
              </div>

              {/* Invoice terms — only when invoicing; moot if paid on the spot */}
              {settle === 'invoice' && (
              <div>
                <h4 className="text-[13px] font-semibold text-gray-900 mb-1">Invoice terms</h4>
                <p className="text-[11px] text-gray-500 mb-2">
                  {defaultTerm.source === 'customer'
                    ? `This customer's default is ${termLabel(defaultTerm.term)}.`
                    : `Business default is ${termLabel(defaultTerm.term)}.`}{' '}
                  Change it here for a one-off.
                </p>
                <div className="flex gap-2">
                  <select
                    value={terms}
                    onChange={(e) => setTerms(e.target.value as InvoiceTermCode | 'default')}
                    className="flex-1 px-3 py-2 text-[13px] border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                  >
                    <option value="default">
                      {defaultTerm.source === 'customer' ? 'Customer default' : 'Business default'} ({termLabel(defaultTerm.term)})
                    </option>
                    {DEFAULT_TERM_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                    <option value="custom">Custom due date…</option>
                  </select>
                  {effectiveTerm === 'custom' && (
                    <input
                      type="date"
                      value={customDue}
                      onChange={(e) => setCustomDue(e.target.value)}
                      className="px-3 py-2 text-[13px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                    />
                  )}
                </div>
                {dueDatePreview && (
                  <p className="text-[11px] text-gray-500 mt-1.5">Payment due {dueDatePreview}.</p>
                )}
              </div>
              )}
            </div>
            <div className="px-5 py-4 border-t border-gray-100 flex-shrink-0 space-y-2">
              <button
                disabled={busy || !quote || (settle === 'invoice' && !termsReady)}
                onClick={() => handleInvoice(settle)}
                className={`w-full py-3 rounded-xl text-white text-[14px] font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2 ${
                  settle === 'pay' ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {settle === 'pay'
                  ? `Continue to payment — $${total.toFixed(2)}`
                  : `Send invoice — $${total.toFixed(2)}`}
              </button>
              <p className="text-[11px] text-gray-400 text-center">
                {settle === 'pay'
                  ? 'Issues the invoice, deducts stock, and opens the payment options.'
                  : 'Issues the invoice, deducts stock, and emails it to the customer with the PDF and a payment link.'}
                {invoiceOnly && ' The order stays in the pipeline for delivery.'}
              </p>
            </div>
          </>
        )}
    </SidePanel>
  );
}
